var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Quaternion as BABYLON_Quaternion, Vector3 as BABYLON_Vector3 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
import { Color4 as BABYLON_Color4 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.color.js';
import { Engine as BABYLON_Engine } from '@inter-ikea-betrakta-babylonjs/core/Engines/engine.js';
import { Scene as BABYLON_Scene } from '@inter-ikea-betrakta-babylonjs/core/scene.js';
import { DracoCompression as BABYLON_DracoCompression } from '@inter-ikea-betrakta-babylonjs/core/Meshes/Compression/dracoCompression.js';
import { PushMaterial as BABYLON_PushMaterial } from '@inter-ikea-betrakta-babylonjs/core/Materials/pushMaterial.js';
import { Ray as BABYLON_Ray } from '@inter-ikea-betrakta-babylonjs/core/Culling/ray.js';
import { Texture as BABYLON_Texture } from '@inter-ikea-betrakta-babylonjs/core/Materials/Textures/texture.js';
import { NormalMaterial as BABYLON_NormalMaterial } from '@inter-ikea-betrakta-babylonjs/materials/normal/normalMaterial.js';
import { MaterialHelper as BABYLON_MaterialHelper } from '@inter-ikea-betrakta-babylonjs/core/Materials/materialHelper.js';
import { take_screenshot } from './Screenshots.js';
import { ReflectionProbe } from './experimental/ReflectionProbe.js';
// The following imports are needed for side effects
import './shaders/ShadersInclude/imageProcessingFunctions.js';
import '@inter-ikea-betrakta-babylonjs/core/Collisions/collisionCoordinator.js';
import '@inter-ikea-betrakta-babylonjs/core/Lights/Shadows/shadowGeneratorSceneComponent.js';
import '@inter-ikea-betrakta-babylonjs/core/Rendering/depthRendererSceneComponent.js'; // Temporal Effects
import '@inter-ikea-betrakta-babylonjs/core/Misc/screenshotTools.js'; // Screenshots
import '@inter-ikea-betrakta-babylonjs/core/Materials/Textures/Loaders/ddsTextureLoader.js'; // .DDS loader for Environment maps
import '@inter-ikea-betrakta-babylonjs/core/Materials/Textures/Loaders/envTextureLoader.js'; // .ENV loader for Environment maps
import '@inter-ikea-betrakta-babylonjs/core/Materials/Textures/Loaders/ktxTextureLoader.js'; // .KTX loader for basis textures in gltfs
import '@inter-ikea-betrakta-babylonjs/core/Animations/animatable.js'; // Animations
// TODO: Here is where we could add KTX or BASIS-support by importing appropriate loaders
// TODO: Not sure how to make .HDR-textures supported... Can't find a loader for it
import { CameraMode, IBoundingBox, IColor, IColor4, IMatrix4, ViewerOptionalFeatures, RenderLayerAllocation, CameraTransitionPreset, ITransformedBoundingBox } from '../api.js';
import { AssetManager, debug_download_texture, download_gltf_files } from './AssetManager.js';
import { LightBaker } from './LightBaker.js';
import { OutlineLayer } from './OutlineEffect.js';
import { ViewerMaterial } from './material/ViewerMaterial.js';
import { LineManager } from './LineManager.js';
import { MeshManager } from './MeshManager.js';
import { MaterialFreezingManager } from './MaterialFreezingManager.js';
import { AnimationSystem } from './AnimationSystem.js';
import { clamp, degrees, radians } from '../Math.js';
import { EXPOSURE_PRESETS, ev_to_intensity, exposure_value } from './PostProcessManager.js';
import { icolor_to_col4, vector_3 } from './utility/Conversion.js';
import { get_or_set_map_value, update_from_partial } from './utility/utils.js';
import { BabylonEngineCamera } from './camera/EngineCamera.js';
import { TRANSITION_PRESETS } from './camera/CameraManager.js';
import { Observable } from '../Observable.js';
import { FocusBorder } from './FocusBorder.js';
import { InteractionSystem } from './InteractionSystem.js';
import { CoroutineManager } from '../CoroutineManager.js';
import { TextField } from './TextField.js';
import { get_perspective_framed_bounds, get_precise_framed_perspective_camera_matrix } from './camera/ObjectFraming.js';
import { Framebuffer, ViewerFramebufferType } from './Framebuffer.js';
import { ImageProcessingConfiguration as BABYLON_ImageProcessingConfiguration } from '@inter-ikea-betrakta-babylonjs/core/Materials/imageProcessingConfiguration.js';
import { GpuProfiler } from './utility/GpuProfiler.js';
import { LightManager } from './light-manager/LightManager.js';
import { get_object_bounds_local, get_object_bounds_world, is_valid_bounding_box, update_aabb_by_transformed_aabb } from './utility/Bounds.js';
import { CutoutObject, CutoutShape } from './CutoutObject.js';
import { get_hardware_limits } from './utility/HardwareLimits.js';
import { RENDER_LAYER_MASK_ALL, RENDER_LAYER_MASK_DEFAULT, RENDER_LAYER_SHADOW, RENDER_LAYER_UI, RenderLayerMask } from './RenderLayer.js';
import { ViewCompositor } from './utility/ViewCompositor.js';
import { RenderTargetTexture as BABYLON_RenderTargetTexture } from '@inter-ikea-betrakta-babylonjs/core/Materials/Textures/renderTargetTexture.js';
import { ShaderLanguage as BABYLON_ShaderLanguage } from '@inter-ikea-betrakta-babylonjs/core/Materials/shaderLanguage.js';
import { ThinEngine as BABYLON_ThinEngine } from '@inter-ikea-betrakta-babylonjs/core/Engines/thinEngine.js';
import { assert } from './utility/Assert.js';
import { filter_closest_hit, filter_hits_to_one_per_interactable_in_place, raycast_all_internal, raycast_all_objects_internal, raycast_all_gizmos_internal, update_babylon_ray } from './Raycast.js';
import { get_fitted_inverse_tonemap, srgb_to_rrt_sat } from '../utils/InverseTonemapper.js';
import { WebGL2Mock, webgl2_capabilities_mac_m2_chrome } from './WebGLMock.js';
import { Deprecated } from '../utils/Deprecated.js';
import { UiMesh } from './UiMesh.js';
import { SoftShadowRenderer } from './SoftShadowRenderer.js';
import { ExceptionHandler } from '../logging/exception_handler/ExceptionHandler.js';
import { Logger } from '../logging/Logger.js';
import { ViewerXRSession } from './webxr/ViewerXRSession.js';
import { WebGLMemoryTracker } from './WebGLMemoryTracker.js';
import { View } from './View.js';
import { InputHandlerManager } from '../input/InputHandlerManager.js';
import { ViewerLockSystem } from './utility/LockSystem.js';
import { BaseTexture as BABYLON_BaseTexture } from '@inter-ikea-betrakta-babylonjs/core/Materials/Textures/baseTexture.js';
import { ViewerScene } from './scene/ViewerScene.js';
import { BetraktaGLTFLoaderExtension } from './scene/BetraktaGLTFLoaderExtension.js';
import { LutEffect } from './LutEffect.js';
import { fake_image_loader } from './utility/FakeImageLoader.js';
import { GhostMaterial } from './material/GhostMaterial.js';
import { get_file_extension } from '../utils/FilePath.js';
import { BetraktaPBRMaterialImplementation } from './material/BetraktaPBRMaterial.js';
export const VIEWER_DEFAULT_FLOAT_FRAMEBUFFER_TYPE = ViewerFramebufferType.R11_G11_B10F;
export const VIEWER_DEFAULT_COMPATIBILITY_FRAMEBUFFER_TYPE = ViewerFramebufferType.RGBA8;
export const VIEWER_WANTED_MSAA_SAMPLES = 4;
// Replaced by correct data at build-time
const VIEWER_VERSION = '11.1.0';
const COMMIT_HASH = '#27210bcc4';
const PRINT_COMMIT_HASH = false; // Set to false if building release version
export var RenderingGroupID;
(function (RenderingGroupID) {
    RenderingGroupID[RenderingGroupID["SKYBOX_RENDERING_GROUP"] = 1] = "SKYBOX_RENDERING_GROUP";
    RenderingGroupID[RenderingGroupID["DEFAULT_RENDERING_GROUP"] = 2] = "DEFAULT_RENDERING_GROUP";
})(RenderingGroupID || (RenderingGroupID = {}));
const FRAMES_TO_COMPOSITE = 4;
const MAX_RENDER_SIZE = 16384;
class TimeDelta {
    constructor(seconds) {
        this._delta_seconds = seconds;
        this._total_time_seconds = 0;
        this._elapsed_frames = 0;
    }
    get milliseconds() {
        return this.seconds * 1000.0;
    }
    get seconds() {
        return this._delta_seconds;
    }
    set seconds(seconds) {
        this._delta_seconds = seconds;
    }
    get total_time_seconds() {
        return this._total_time_seconds;
    }
    get elapsed_frames() {
        return this._elapsed_frames;
    }
    increment_elapsed_frames() {
        this._elapsed_frames += 1;
    }
    increase_total_time(seconds) {
        this._total_time_seconds += seconds;
    }
    reset_total_time() {
        this._total_time_seconds = 0;
    }
    get total_time_milliseconds() {
        return this._total_time_seconds * 1000.0;
    }
}
export class Viewer {
    constructor(canvas_element, config, optional_features_partial) {
        var _a;
        this.canvas_element = canvas_element;
        this._option_enable_instances = true;
        this._debug_layer = null;
        this._picked_object = null;
        this._baker = null; // TODO: Only allow one baker for now. Add possibility to have multiple bakers later on...
        // If this is false, all frames are rendered (overridden by _pause_rendering and _number_of_forced_frames_to_render)
        this.only_render_on_demand = true;
        // This should be marked true whenever we need to clear the backbuffer, such as after a resize or a View has changed its viewport on the canvas
        this._backbuffer_clear_dirty = true;
        this._coroutine_manager = new CoroutineManager();
        // TODO: This should be revisited when we implement none namespaced metadata handling
        this._object_metadata_store = new Map();
        this._object_focus_borders = new Map();
        this._scene_bounds = { min: { x: 0, y: 0, z: 0 }, max: { x: 0, y: 0, z: 0 } };
        this._scene_bounds_changed_observable = new Observable();
        this._objects_changed_observable = new Observable();
        this._object_deleted_observable = new Observable();
        this._update_observable = new Observable();
        // These are for forwarding the default view's observables, where we don't want the user to get the view as the parameter, and instead get the Viewer.
        this._default_view_before_render_observable = new Observable();
        this._default_view_after_render_observable = new Observable();
        this._default_view_camera_updated_observable = new Observable();
        this._default_view_active_camera_changed_observable = new Observable();
        this._default_view_camera_transition_begin_observable = new Observable();
        this._default_view_camera_transition_end_observable = new Observable();
        this._before_render_scene_setup_observable = new Observable();
        // For keeping track of all the objects which have had their transform updated since last frame. Cleared each frame.
        this._updated_objects_this_frame = new Set();
        this._object_changed_observables = new Map();
        this._gpu_profiler = null;
        this._scene_dirty = true;
        this._custom_views = [];
        this._view_composition = [];
        this._delta_time_object = new TimeDelta(0);
        this._optional_features = new ViewerOptionalFeatures();
        this._click_void_observable = new Observable();
        this._secondary_click_void_observable = new Observable();
        // Used when fetching current FPS to know if we should return 0, or our actual FPS.
        this._last_frame_was_composited = false;
        this._rendering_is_paused = false;
        this._forced_render_frame_count = 0;
        this._rendering_lock_system = new ViewerLockSystem();
        this._viewer_resize_observable = new Observable();
        this._viewer_dispose_observable = new Observable();
        this._shadowmap_render_layer_mask = RENDER_LAYER_MASK_ALL;
        this._exception_handler = new ExceptionHandler();
        this._background_color = { r: 0, g: 0, b: 0 };
        this.interactable_disposed_observable = new Observable();
        // State variable to know if we are currently trying to initialize an XR session
        this._is_initializing_xr = false;
        // Our XR session, if any.
        this.xr = null;
        this._webgl_memory_tracker = null;
        this._input_handler_manager = new InputHandlerManager();
        this._world_bounds_is_dirty = false;
        this._should_notify_world_bounds = false;
        this.reflection_probes = [];
        this._effect_compiled_promises = new Set();
        this._has_been_disposed = false;
        // TODO: Temporary cache before we fix Asset Manager stuff. Retains old behaviour.
        this._loaded_material_map = new Map();
        Logger.setup({
            name: 'Betrakta'
        });
        Logger.set_enabled(true);
        if (Viewer._viewer_instances_running > 0) {
            Logger.error('Multiple (' + Viewer._viewer_instances_running + ') simultaneous instances of Viewer already running, this is not supported and may fail.');
        }
        Viewer._viewer_instances_running++;
        // Workaround for inspector accessign NormalMaterial via CDN if it is not available
        if (typeof window !== 'undefined') {
            if (window.BABYLON === undefined) {
                window.BABYLON = {};
            }
            window.BABYLON.NormalMaterial = BABYLON_NormalMaterial;
        }
        this._optional_features = update_from_partial(new ViewerOptionalFeatures(), optional_features_partial !== null && optional_features_partial !== void 0 ? optional_features_partial : {});
        this._config = config;
        // Workaround for Chrome bug on Android
        // See: https://forum.babylonjs.com/t/problems-on-chrome-mobile-since-december-7th-2023/46288/15
        BABYLON_Engine.ExceptionList.push({ key: 'Chrome/12\\d\\..+?Mobile', capture: null, captureConstraint: null, targets: ['uniformBuffer'] });
        const original_prepare_defines_for_light = BABYLON_MaterialHelper.PrepareDefinesForLight;
        const light_defines_override = (scene, mesh, light, light_index, defines, specular_supported, state, ...args) => {
            defines['LINELIGHT' + light_index] = false;
            // @ts-ignore
            original_prepare_defines_for_light(scene, mesh, light, light_index, defines, specular_supported, state, ...args);
        };
        // @ts-ignore
        if (BABYLON_MaterialHelper.light_defines_override === undefined) {
            // @ts-ignore
            BABYLON_MaterialHelper.light_defines_override = light_defines_override;
            // Make calls to PrepareDefinesForLight call the new function
            // @ts-ignore
            BABYLON_MaterialHelper.PrepareDefinesForLight = BABYLON_MaterialHelper.light_defines_override;
        }
        if (this._config.debug_force_webgl1) {
            // If someone has disabled WebGL2 explicitly, let them know.
            Logger.warn("WARNING: Betrakta WebGL2 support explicitly disabled. Do not use in production.");
        }
        this.canvas_element = canvas_element;
        const disable_webgl2 = this._config.debug_force_webgl1 && !this._config.headless;
        if (this._config.debug_force_webgl1 && this._config.headless) {
            Logger.warn('Betrakta Viewer was requested headless & without WebGL2 support, these options are incompatible. Viewer will be created headless with WebGL 2 support.');
        }
        let gl_context = null;
        // See: https://developer.mozilla.org/en-US/docs/Web/API/HTMLCanvasElement/getContext
        const options = {
            xrCompatible: this._config.xr_compatible,
            antialias: false,
            alpha: true,
            depth: false,
            desynchronized: undefined,
            failIfMajorPerformanceCaveat: undefined,
            powerPreference: 'default',
            premultipliedAlpha: false,
            preserveDrawingBuffer: true,
            stencil: false
        };
        if (this._config.headless) {
            const mock = new WebGL2Mock(this.canvas_element, this.canvas_element.clientWidth, this.canvas_element.clientHeight, webgl2_capabilities_mac_m2_chrome, options);
            gl_context = mock;
        }
        else {
            if (!disable_webgl2) {
                const context = (this.canvas_element.getContext('webgl2', options) ||
                    this.canvas_element.getContext('experimental-webgl2', options));
                if (context) {
                    gl_context = context;
                }
            }
            if (gl_context === null) {
                gl_context = (this.canvas_element.getContext('webgl', options) ||
                    this.canvas_element.getContext('experimental-webgl', options));
            }
            if (gl_context === null) {
                throw new Error('WebGL not supported');
            }
        }
        if (config.enable_webgl_memory_tracker) {
            this._webgl_memory_tracker = new WebGLMemoryTracker(gl_context, this);
        }
        // Since we use our own framebuffer, the canvas framebuffer doesn't need antialiasing.
        const engine_antialiasing = false;
        // mock as WebGLRenderingContext
        this._engine = new BABYLON_Engine(gl_context, engine_antialiasing, Object.assign(Object.assign({}, options), { disableWebGL2Support: disable_webgl2 })); // Babylon crashes on Safari 15 if xrCompatibility is enabled
        if (!this._config.use_parallel_shader_compile) {
            // Disable parallel shader compile extension (this is Babylon's recommended way of doing it)
            this._engine.getCaps().parallelShaderCompile = undefined;
        }
        // Workarounds for Babylon bugs.
        // See the links in the functions for their reports and maybe they have been fixed when you update Babylon :)
        this.fix_babylon_msaa_memory_leak();
        this.fix_babylon_msaa_texture_formats();
        // End of Babylon bug workarounds
        // Overload the engine's createEffect function to dirty the viewer whenever shaders finish compilation (an issue when we have parallel shader compilation)
        this.overload_babylon_create_effect();
        const scene_options = {
            // If this isn't set to false, we get some huge memory leaks that cause really big GC spikes in extreme cases.
            // Found when deleting & creating 50 custom meshes every frame.
            useMaterialMeshMap: false
        };
        this._scene = new BABYLON_Scene(this.engine, scene_options);
        this.scene.useRightHandedSystem = true;
        this.scene.preventDefaultOnPointerDown = false;
        this.scene.preventDefaultOnPointerUp = false;
        this.scene.doNotHandleCursors = true;
        this.scene.skipPointerDownPicking = true;
        this.scene.skipPointerMovePicking = true;
        this.scene.skipPointerUpPicking = true;
        this.scene.clearColor = new BABYLON_Color4(0.95, 0.95, 0.95, 1);
        this.freezing_manager = new MaterialFreezingManager(this.scene, () => !!(this._baker && this._baker.running));
        this.freezing_manager.enabled = false;
        BetraktaGLTFLoaderExtension.register_extension(this);
        this.viewer_scene = new ViewerScene(this, this.scene);
        if (config.headless) {
            // With js-dom, it seems like it doesn't load image elements. This causes materials that load textures
            // (such as every PBR material, as they load a built-in environment map), to never be ready.
            // This blocks rendering, meaning we can't test any rendering.
            // So to work around it, we implement our own image loader that loads a simple image with fake data.
            BABYLON_ThinEngine._FileToolsLoadImage = fake_image_loader;
        }
        BABYLON_BaseTexture.DEFAULT_ANISOTROPIC_FILTERING_LEVEL = config.anisotropic_filtering_level;
        this._compositing_frames_remaining = FRAMES_TO_COMPOSITE;
        const canvas_width = this.engine.getRenderWidth(true);
        const canvas_height = this.engine.getRenderHeight(true);
        this._canvas_render_width = canvas_width;
        this._canvas_render_height = canvas_height;
        this._interaction_system = new InteractionSystem(this, (_a = this._config.interaction_system_enabled) !== null && _a !== void 0 ? _a : true);
        if (this._config.debug_enable_gpu_profiler) {
            this.enable_gpu_profiler();
        }
        // Create our "null" camera and render target here to be able to call Babylon's scene.render() to perform shadow maps and other things, without actually rendering the scene.
        // See explanation at the declaration of these variables what this is for and how it works.
        // Create our "null" camera and assign it to the scene
        this._null_babylon_camera = new BabylonEngineCamera('', this.scene, false);
        this.scene.activeCamera = this._null_babylon_camera;
        this.scene.activeCamera.setEnabled(false);
        // Create the "null" render target and assign it to the "null" camera
        this._null_babylon_rendertarget = new BABYLON_RenderTargetTexture('null viewer rt', { width: 4, height: 4 }, this.scene);
        // Make sure it never renders
        this._null_babylon_rendertarget._shouldRender = () => { return false; };
        this.scene.activeCamera.outputRenderTarget = this._null_babylon_rendertarget;
        // Create the default view
        if (this._config.create_default_view) {
            const use_float_framebuffer_type = this._config.use_hdr_framebuffer &&
                Framebuffer.is_float_framebuffer_supported(this._engine);
            this._default_view = new View(this, this._interaction_system, canvas_width, canvas_height, use_float_framebuffer_type ? VIEWER_DEFAULT_FLOAT_FRAMEBUFFER_TYPE : VIEWER_DEFAULT_COMPATIBILITY_FRAMEBUFFER_TYPE, VIEWER_WANTED_MSAA_SAMPLES);
            this._float_framebuffer_enabled = this._default_view.framebuffer.is_float_render_target();
            this._view_composition.push({ view: this._default_view, z_index: 0 });
            // In order to not expose the View for this seemingly "Viewer" observable, we create a "bridge" observable.
            this._default_view.before_render_observable.add(() => this._default_view_before_render_observable.notify(this));
            this._default_view.after_render_observable.add(() => this._default_view_after_render_observable.notify(this));
            this._default_view.camera_updated_observable.add((_, camera) => this._default_view_camera_updated_observable.notify(camera));
            this._default_view.camera_manager.active_camera_changed_observable.add((_, camera) => this._default_view_active_camera_changed_observable.notify(this, camera));
            this._default_view.camera_manager.camera_transition_begin_observable.add((_, event) => this._default_view_camera_transition_begin_observable.notify(this, event));
            this._default_view.camera_manager.camera_transition_end_observable.add((_, event) => this._default_view_camera_transition_end_observable.notify(this, event));
        }
        else {
            this._default_view = null;
            this._float_framebuffer_enabled =
                this._config.use_hdr_framebuffer &&
                    Framebuffer.is_float_framebuffer_supported(this._engine);
        }
        // Base exposure value to match the lighting we had before tonemapping
        this.scene.imageProcessingConfiguration.exposure = 1.4;
        /*
          Here we cover the case where we cannot use our Tonemapping effect post process,
          and instead use Babylons built-in functionality where tone mapping is done in the forward pass.
          If our custom tone mapping is supported it is applied in postprocess.
        */
        if (!this._float_framebuffer_enabled) {
            // We want tonemapping, but float framebuffer wasn't supported.
            this.scene.imageProcessingConfiguration.toneMappingEnabled = true;
            this.scene.imageProcessingConfiguration.toneMappingType = BABYLON_ImageProcessingConfiguration.TONEMAPPING_ACES;
        }
        // Set up render layer allocations
        // The Viewer reserves bits [0...7] for internal use
        // We purposefully "underallocate" and leave a lot of bits unused.
        // This is so we can give more allocations to different users in the future, without taking anyone else's away, depending on need.
        // IMPORTANT NOTE: Bit 31 does NOT work. Babylon doesn't like it as it produces a negative number.
        this._layer_allocation_map = new Map();
        this._layer_allocation_map.set(RenderLayerAllocation.IPEX_CORE, [8, 9, 10, 11]);
        this._layer_allocation_map.set(RenderLayerAllocation.IPEX_KVADRAT, [12, 13, 14]);
        this._layer_allocation_map.set(RenderLayerAllocation.IPEX_SPACE, [15, 16, 17]);
        this._layer_allocation_map.set(RenderLayerAllocation.IPEX_PLANNER, [18, 19, 20]);
        this._view_compositor = new ViewCompositor(this.scene);
        const ubo_supported = this._engine.supportsUniformBuffers;
        ViewerMaterial.create_shaders(ubo_supported);
        BABYLON_DracoCompression.Configuration = {
            decoder: this._config.draco_decoder
        };
        this.set_background_color(0.95, 0.95, 0.95);
        this.line_manager = new LineManager(this, RENDER_LAYER_UI, false);
        this._line_manager_on_top = new LineManager(this, RENDER_LAYER_UI, true);
        // NOTE: This ties our asset manager to one scene, but that is hard to avoid with the ownership model Babylon has anyway
        this.asset_manager = new AssetManager(this.scene, this.freezing_manager, () => this.mark_scene_dirty(), this);
        this._light_manager = new LightManager(this);
        this.mesh_manager = new MeshManager(this, this.asset_manager, this._light_manager.light_system, () => this.mark_scene_dirty());
        this.animation_system = new AnimationSystem(this);
        this.scene.onNewMaterialAddedObservable.add((material) => {
            // Enabling hot swapping will cause some non-critical webgl-warnings, but will reduce blinking glitches.
            // See https://forum.babylonjs.com/t/light-dispose-causes-webgl-errors/11176
            // We disable swapping for now.
            if (material instanceof BABYLON_PushMaterial) {
                material.allowShaderHotSwapping = false;
            }
        });
        this._object_changed_observer = this.viewer_scene.object_changed_observable.add((_, object) => {
            this._updated_objects_this_frame.add(object.id);
            this.mark_world_bounds_dirty();
        });
        this._object_removed_observer = this.viewer_scene.object_removed_observable.add((_, object) => {
            this.on_object_removed(object);
            this._object_deleted_observable.notify(this, object.id);
            this._object_changed_observables.delete(object.id);
        });
        this.outline_layer = new OutlineLayer('Outline layer', this.scene);
        if (config.run_render_loop) {
            this._engine.runRenderLoop(() => {
                this.update(this._engine.getDeltaTime() / 1000.0);
            });
        }
        // This disables frustum culling on submesh-level
        this.scene._activeMeshesFrozenButKeepClipping = false;
        if (LutEffect.is_supported(this._engine)) {
            this.lut_effect = new LutEffect(this);
        }
        else {
            this.lut_effect = null;
        }
        Logger.info(`Betrakta viewer v${VIEWER_VERSION}${PRINT_COMMIT_HASH ? (' - ' + COMMIT_HASH) : ''}`);
    }
    get_lut_effect() {
        return this.lut_effect;
    }
    get_logger() {
        return Logger;
    }
    enter_xr_session(mode, reference_space_type) {
        if (this.xr !== null) {
            return Promise.reject(new Error('Viewer already has active XR session'));
        }
        if (this._is_initializing_xr) {
            return Promise.reject(new Error('Viewer is already initializing an XR session'));
        }
        if (!this._config.xr_compatible) {
            return Promise.reject(new Error('Viewer was not initialized with XR compatibility in ViewerConfiguration'));
        }
        if (!navigator.xr) {
            return Promise.reject(new Error('XR not available in this browser context.'));
        }
        this._is_initializing_xr = true;
        return new Promise((resolve, reject) => {
            navigator.xr.requestSession(mode).then((session) => {
                const gl = this._engine._gl;
                const layer = new XRWebGLLayer(session, gl);
                session.updateRenderState({
                    baseLayer: layer
                });
                session.requestReferenceSpace(reference_space_type).then((reference_space) => {
                    const xr = new ViewerXRSession(this, session, layer, reference_space);
                    xr.on_end_observable.add_once(() => {
                        if (this.xr === xr) {
                            this.xr = null;
                        }
                        else {
                            Logger.warn("Warning: Betrakta XR session was not the session that ended. This should not be able to happen.");
                        }
                    });
                    this.xr = xr;
                    this._is_initializing_xr = false;
                    resolve(this.xr);
                }).catch((reason) => {
                    this._is_initializing_xr = false;
                    reject(reason);
                });
            }).catch((reason) => {
                this._is_initializing_xr = false;
                reject(reason);
            });
        });
    }
    is_xr_session_mode_supported(mode) {
        if (navigator.xr) {
            return navigator.xr.isSessionSupported(mode);
        }
        else {
            return Promise.resolve(false);
        }
    }
    fix_babylon_msaa_texture_formats() {
        // Silly hack to fix a Babylon function that wouldn't take the R11F_G11F_B10F format into account.
        // Used by the Framebuffer.
        // Bug reported here: https://forum.babylonjs.com/t/using-msaa-causes-opengl-errors-with-certain-color-renderable-formats/45994
        const gl = this._engine._gl;
        const original_engine_function = this._engine._getRGBAMultiSampleBufferFormat.bind(this._engine);
        this._engine._getRGBAMultiSampleBufferFormat = (type) => {
            if (type === BABYLON_Engine.TEXTURETYPE_UNSIGNED_INT_10F_11F_11F_REV) {
                return gl.R11F_G11F_B10F;
            }
            else {
                return original_engine_function(type);
            }
        };
    }
    fix_babylon_msaa_memory_leak() {
        // Reported here: https://forum.babylonjs.com/t/babylon-leaks-msaa-renderbuffers-on-resize-dispose-with-webgl/51228
        const original_release_texture = this._engine._releaseTexture.bind(this._engine);
        this._engine._releaseTexture = (texture) => {
            var _a;
            (_a = texture._hardwareTexture) === null || _a === void 0 ? void 0 : _a.release();
            original_release_texture(texture);
        };
    }
    get_effects_compiled_promise() {
        const promises = [];
        this._effect_compiled_promises.forEach(promise => {
            promises.push(promise);
        });
        return new Promise((resolve) => {
            Promise.allSettled(promises).then(() => resolve());
        });
    }
    overload_babylon_create_effect() {
        // Here we overload the Babylon engine's function 'createEffect', so that we can hook up on all onCompileObservable for all effects created in the engine.
        // This is so we can dirty the Viewer whenever ANY shader has finished compiling.
        // It's an ugly hack, but it's something we need for our 'render-on-demand' solution.
        const original_create_effect = this._engine.createEffect.bind(this._engine);
        // Keep track of which effects we're already tracking compilation on. Babylon may return a cached and already compiled effect if you request an effect of the same source code twice.
        const registered_effects = new Set();
        this._engine.createEffect = (
        /* eslint-disable */
        baseName, attributesNamesOrOptions, uniformsNamesOrEngine, samplers, defines, fallbacks, onCompiled, onError, indexParameters, shaderLanguage = BABYLON_ShaderLanguage.GLSL
        /* eslint-enable */
        ) => {
            const effect = original_create_effect(baseName, attributesNamesOrOptions, uniformsNamesOrEngine, samplers, defines, fallbacks, onCompiled, onError, indexParameters, shaderLanguage);
            // Make sure we don't register twice for the same effect.
            if (registered_effects.has(effect))
                return effect;
            registered_effects.add(effect);
            // We need to be notified when that effect has finished compiling and dirty the Viewer.
            // If the effect is already ready, then dirty the Viewer immediately.
            if (effect.isReady()) {
                this.mark_scene_dirty();
            }
            else {
                // Create a promise to resolve once the shader has compiled or failed to compile.
                const effect_compiled_promise = new Promise((resolve, reject) => {
                    effect.onCompileObservable.addOnce(() => {
                        resolve();
                        this._effect_compiled_promises.delete(effect_compiled_promise);
                    });
                    effect.onErrorObservable.add(() => {
                        reject(new Error('Shader compilation failed.'));
                        this._effect_compiled_promises.delete(effect_compiled_promise);
                    });
                });
                this._effect_compiled_promises.add(effect_compiled_promise);
            }
            // Either way if it was already compiled or not, we want to dirty if the shader was recompiled.
            // Not sure if this actually ever happens, but see it as a failsafe.
            effect.onCompileObservable.add(() => this.mark_scene_dirty());
            effect.onErrorObservable.add(() => this.mark_scene_dirty());
            return effect;
        };
    }
    /**
     * Internal
     * @returns Babylon scene reference
     */
    get scene() {
        return this._scene;
    }
    get engine() {
        return this._engine;
    }
    get light_manager() {
        return this._light_manager;
    }
    get light_system() {
        return this._light_manager.light_system;
    }
    get_crash_report() {
        let babylon_shader_cache_count = 0;
        let babylon_shader_cache_waste_bytes = 0;
        try {
            // @ts-expect-error
            const effects = this._engine._compiledEffects;
            const keys = Object.keys(effects);
            babylon_shader_cache_count = 0;
            keys.forEach((key) => {
                const effect = effects[key];
                babylon_shader_cache_count++;
                babylon_shader_cache_waste_bytes += key.length;
                babylon_shader_cache_waste_bytes += effect._fragmentSourceCode.length;
                babylon_shader_cache_waste_bytes += effect._vertexSourceCode.length;
                babylon_shader_cache_waste_bytes += effect.fragmentSourceCodeBeforeMigration.length;
                babylon_shader_cache_waste_bytes += effect.vertexSourceCodeBeforeMigration.length;
                // @ts-expect-error
                babylon_shader_cache_waste_bytes += effect._fragmentSourceCodeOverride.length;
                // @ts-expect-error
                babylon_shader_cache_waste_bytes += effect._vertexSourceCodeOverride.length;
            });
        }
        catch (e) { }
        return {
            viewer_version: VIEWER_VERSION,
            viewer_commit_hash: COMMIT_HASH,
            scene: this.viewer_scene.get_debug_information(),
            babylon_shader_cache_shader_count: babylon_shader_cache_count,
            babylon_shader_cache_memory_usage: babylon_shader_cache_waste_bytes,
            webgl_memory_usage: this._webgl_memory_tracker === null ? null : this._webgl_memory_tracker.get_memory_usage_report()
        };
    }
    get_view_at_canvas_coordinates(x, y) {
        this.sort_view_composition();
        for (let i = this._view_composition.length - 1; i >= 0; i--) {
            const view = this._view_composition[i].view;
            const canvas_viewport = view.get_canvas_viewport();
            if (x > canvas_viewport.x &&
                y > canvas_viewport.y &&
                x < canvas_viewport.x + canvas_viewport.width &&
                y < canvas_viewport.y + canvas_viewport.height) {
                return view;
            }
        }
        return null;
    }
    create_view(width, height) {
        const view = new View(this, this._interaction_system, width, height, this._float_framebuffer_enabled ? VIEWER_DEFAULT_FLOAT_FRAMEBUFFER_TYPE : VIEWER_DEFAULT_COMPATIBILITY_FRAMEBUFFER_TYPE, 4);
        this._custom_views.push(view);
        // To mimic the old behaviour where composition was done default view first and then
        // custom_views in creation order. This will mimic that behaviour given that no z_index is changed.
        this._view_composition.push({ view: view, z_index: this._custom_views.length });
        return view;
    }
    has_default_view() {
        return this._default_view !== null;
    }
    get_default_view() {
        assert(this._default_view !== null, 'Default view is not created');
        return this._default_view;
    }
    delete_view(iview) {
        if (iview === null)
            return;
        if (iview === this._default_view) {
            Logger.warn("Can't delete default view. If you do not want it, use the optional feature: no_default_view when creating the Betrakta Viewer.");
            return;
        }
        const view = iview;
        const i = this._custom_views.indexOf(view);
        if (i >= 0) {
            this._custom_views.splice(i, 1);
            view.dispose();
        }
        else {
            Logger.warn('View not found in custom views.');
        }
    }
    get_view_composition_array() {
        return this._view_composition;
    }
    get_optional_features() {
        return this._optional_features;
    }
    get_config() {
        return this._config;
    }
    check_inside_viewport(client_x, client_y) {
        const rect = this.canvas_element.getBoundingClientRect();
        const default_view = this.get_default_view();
        const viewport = default_view.get_canvas_viewport();
        const client_scale_x = (rect.width / this.get_canvas_render_width());
        const client_scale_y = (rect.height / this.get_canvas_render_height());
        // Convert viewport to client space
        viewport.x = rect.x + viewport.x * client_scale_x;
        viewport.y = rect.y + viewport.y * client_scale_y;
        viewport.width *= client_scale_x;
        viewport.height *= client_scale_y;
        const inside_viewport_x = client_x >= viewport.x && client_x <= viewport.x + viewport.width;
        const inside_viewport_y = client_y >= viewport.y && client_y <= viewport.y + viewport.height;
        return inside_viewport_x && inside_viewport_y;
    }
    get_gpu_profiler() {
        if (this._gpu_profiler === null)
            return null;
        return this._gpu_profiler.profiler;
    }
    enable_gpu_profiler() {
        if (this._gpu_profiler !== null)
            return;
        if (GpuProfiler.is_supported(this.engine._gl)) {
            const profiler = new GpuProfiler(this.engine._gl, 32);
            this._gpu_profiler = {
                profiler: profiler,
                section_normal_frame: profiler.create_section('Normal frame'),
                section_composite_frame: profiler.create_section('Composite frame'),
                section_outlines: profiler.create_section('Outlines')
            };
            profiler.enable_visualizer(this, true);
        }
        else {
            Logger.warn("Betrakta: Tried to enable GPU profiler, but it is not supported by this hardware and/or browser. As of writing, Chromium has best support, and phones lack support.");
        }
    }
    disable_gpu_profiler() {
        if (this._gpu_profiler === null)
            return;
        this._gpu_profiler.profiler.dispose();
        this._gpu_profiler = null;
    }
    update(delta_time_seconds) {
        var _a;
        this.scene.incrementRenderId();
        this._delta_time_object.seconds = delta_time_seconds;
        this._delta_time_object.increase_total_time(this._delta_time_object.seconds);
        this._delta_time_object.increment_elapsed_frames();
        this.viewer_scene.update(this._delta_time_object);
        // Animate the scene, in case we're animating anything.
        // Previously this was done in scene.render(), but since we don't call that anymore for camera movements inside a view, we need to call it here.
        // We do this first, to allow any cameras to respond to anything animated in the scene.
        this.scene.animate();
        // Update cameras early so that update handlers and coroutines may depend on correct state
        (_a = this._default_view) === null || _a === void 0 ? void 0 : _a.update(this._delta_time_object);
        for (let i = 0; i < this._custom_views.length; i++) {
            this._custom_views[i].update(this._delta_time_object);
        }
        // This is a failsafe, in case someone has overwritten the activeCamera field in the scene without restoring it.
        // See declaration of these variables for explanation on what they're for.
        this.scene._activeCamera = this._null_babylon_camera;
        this.scene.customRenderTargets = [];
        if (this._default_view !== null) {
            // Copy over camera transforms to our "null" camera, as its the scene's active camera, meaning it's used for click/drag events.
            // This means we only get input for our default view though.
            // We could move to calculate click/drag/picking rays ourselves instead of relying on Babylon.
            // But maybe there's more internals using this that we need.
            this._null_babylon_camera.set_projection_matrix(this._default_view.engine_camera.camera.getProjectionMatrix(), CameraMode.PERSPECTIVE);
            this._null_babylon_camera.set_view_matrix(this._default_view.engine_camera.camera.getViewMatrix());
        }
        this._update_observable.notify(this, this._delta_time_object);
        // TODO: Run this before or after update?
        this._coroutine_manager.update(this._delta_time_object);
        // Notify observers on object changes
        if (this._updated_objects_this_frame.size > 0) {
            this._objects_changed_observable.notify(this, [...this._updated_objects_this_frame]);
            this._updated_objects_this_frame.forEach((object_id) => {
                const observable = this._object_changed_observables.get(object_id);
                if (observable !== undefined) {
                    const state = this.get_object_transform(object_id);
                    observable.notify(object_id, state);
                }
            });
            this._updated_objects_this_frame.clear();
        }
        this.update_bounds_cache();
        if (this._should_notify_world_bounds) {
            this._should_notify_world_bounds = false;
            this._scene_bounds_changed_observable.notify(this, this._scene_bounds);
        }
        const force_render = this._forced_render_frame_count > 0;
        this._forced_render_frame_count = Math.max(0, this._forced_render_frame_count - 1);
        // Forced render frames take precedent over paused rendering.
        if (force_render || !this.is_rendering_paused()) {
            this.render(force_render);
        }
    }
    /**
     * Prepares the data on the scene for render.
     * Should only be done if the scene was dirty.
     *
     * Does not clear the scene dirty.
     */
    prepare_scene_for_render() {
        // We use Babylon's onAfterWorldMatrixUpdateObservable, which usually gets fired when rendering your camera.
        // However, then we can get a one-frame lag of shadow maps, as the shadows might not have been dirtied by Babylon's observable.
        // So this fix makes sure world matrices are up-to-date and can dirty the light system before rendering shadow maps in the light system.
        this.viewer_scene.objects.forEach((object) => {
            if (object.has_render_layer(RENDER_LAYER_SHADOW)) {
                object.mesh_iterator(false, (mesh) => {
                    mesh.computeWorldMatrix(false);
                });
            }
        });
        this.asset_manager.ui_meshes.forEach((gizmo) => {
            gizmo.prepare_for_rendering();
        });
        const light_system_was_dirty = this.light_system.is_dirty();
        // TODO: IC3V-536 Optimize shadows handling, move out of render block
        this.light_system.update();
        this._light_manager.experimental_soft_shadow_renderers.forEach((renderer) => {
            renderer.render();
        });
        // Update all focus borders only if we want to render
        for (const focus_border of this._object_focus_borders.values()) {
            focus_border.update();
        }
        // Make sure to rebuild UI lines before rendering.
        this.line_manager.rebuild_if_dirty();
        this._line_manager_on_top.rebuild_if_dirty();
        // Update the source mesh for all instances based on current state of scene object.
        this.viewer_scene.update_source_mesh_keys();
        // This is some setup needed to process something in Babylon, without it we get incorrect results with material switching when using our own render lists in Views.
        // First disable frustum culling, as we want to do this as lightly as possible.
        this.scene.skipFrustumClipping = true;
        // This function needs to be called for whatever reason, otherwise materials won't always apply/update.
        // Try commenting out and running test scene "Transparency Sorting" and press the assign materials button
        // @ts-ignore
        this.scene._evaluateActiveMeshes();
        // Calling incrementRenderId, because otherwise Babylon renders double instances for us due to the call above to _evaluateActiveMeshes
        this.scene.incrementRenderId();
        for (let i = 0; i < this.reflection_probes.length; i++) {
            this.reflection_probes[i].update(light_system_was_dirty);
        }
    }
    prepare_scene_for_setup() {
        // Fire this before checking scene dirty, to let it dirty the scene if needed
        this._before_render_scene_setup_observable.notify(this);
        // Update the source mesh for all instances based on current state of scene object.
        this.viewer_scene.update_source_mesh_keys();
    }
    render(force_render) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
        this.prepare_scene_for_setup();
        (_a = this._gpu_profiler) === null || _a === void 0 ? void 0 : _a.profiler.start_frame(this._canvas_render_width, this._canvas_render_height);
        (_b = this._gpu_profiler) === null || _b === void 0 ? void 0 : _b.section_normal_frame.start();
        const scene_was_dirty = force_render ||
            this._scene_dirty ||
            this.animation_system.animations_running() ||
            this.light_system.is_dirty() ||
            !this.only_render_on_demand;
        this._scene_dirty = false;
        (_c = this._gpu_profiler) === null || _c === void 0 ? void 0 : _c.profiler.start_frame(this._canvas_render_width, this._canvas_render_height);
        (_d = this._gpu_profiler) === null || _d === void 0 ? void 0 : _d.section_normal_frame.start();
        if (scene_was_dirty) {
            this.prepare_scene_for_render();
        }
        if (scene_was_dirty) {
            // Need to set dirty in case the scene was dirty (for accumulator)
            (_e = this._default_view) === null || _e === void 0 ? void 0 : _e.set_view_dirty();
            (_f = this._default_view) === null || _f === void 0 ? void 0 : _f.postprocess_manager.on_scene_dirty();
            for (let i = 0; i < this._custom_views.length; i++) {
                this._custom_views[i].set_view_dirty();
                this._custom_views[i].postprocess_manager.on_scene_dirty();
            }
        }
        if (this.xr === null) {
            let require_composite = scene_was_dirty || this.outline_layer.get_outlines_are_dirty();
            if ((_g = this._default_view) === null || _g === void 0 ? void 0 : _g.is_dirty()) {
                (_h = this._webgl_memory_tracker) === null || _h === void 0 ? void 0 : _h.new_frame();
                // Update all focus borders only if we want to render default view, as they only work in the default for now.
                for (const focus_border of this._object_focus_borders.values()) {
                    focus_border.update();
                }
                this._default_view.render();
                require_composite = true;
            }
            for (let i = 0; i < this._custom_views.length; i++) {
                if (this._custom_views[i].is_dirty()) {
                    this._custom_views[i].render();
                    require_composite = true;
                }
            }
            if (require_composite) {
                this._compositing_frames_remaining = FRAMES_TO_COMPOSITE;
            }
            if (this._compositing_frames_remaining > 0) {
                (_j = this._gpu_profiler) === null || _j === void 0 ? void 0 : _j.section_composite_frame.start();
                this.engine.restoreDefaultFramebuffer();
                this.engine.setViewport({
                    x: 0,
                    y: 0,
                    width: 1.0,
                    height: 1.0
                }, this.get_canvas_render_width(), this.get_canvas_render_height());
                if (this._backbuffer_clear_dirty) {
                    this.engine.clear({
                        r: this._background_color.r,
                        g: this._background_color.g,
                        b: this._background_color.b,
                        a: 1.0
                    }, true, false, false);
                }
                this.sort_view_composition();
                for (let i = 0; i < this._view_composition.length; i++) {
                    this._view_compositor.render(this._view_composition[i].view, this.outline_layer);
                }
                this._compositing_frames_remaining--;
                if (this._compositing_frames_remaining === 0) {
                    this._backbuffer_clear_dirty = false;
                }
                this.outline_layer.clear_outlines_dirty();
                this._last_frame_was_composited = true;
                (_k = this._gpu_profiler) === null || _k === void 0 ? void 0 : _k.section_composite_frame.stop();
            }
            else {
                this._last_frame_was_composited = false;
            }
            (_l = this._gpu_profiler) === null || _l === void 0 ? void 0 : _l.section_normal_frame.stop();
            (_m = this._gpu_profiler) === null || _m === void 0 ? void 0 : _m.profiler.end_frame();
        }
    }
    sort_view_composition() {
        this._view_composition.sort((a, b) => {
            return a.z_index > b.z_index ? 1 : a.z_index === b.z_index ? 0 : -1;
        });
    }
    dispose() {
        var _a, _b, _c, _d;
        // Early out in case we've already been disposed
        if (this._has_been_disposed)
            return;
        this._has_been_disposed = true;
        (_a = this._debug_layer) === null || _a === void 0 ? void 0 : _a.dispose();
        this._debug_layer = null;
        window['viewer_debug_layer'] = null;
        BetraktaGLTFLoaderExtension.unregister_extension();
        (_b = this.lut_effect) === null || _b === void 0 ? void 0 : _b.dispose();
        this._viewer_dispose_observable.notify(this);
        this._engine.stopRenderLoop();
        this._light_manager.experimental_soft_shadow_renderers.forEach((renderer) => {
            renderer.dispose();
        });
        this._light_manager.experimental_soft_shadow_renderers.clear();
        (_c = this._gpu_profiler) === null || _c === void 0 ? void 0 : _c.profiler.dispose();
        (_d = this._default_view) === null || _d === void 0 ? void 0 : _d.dispose();
        for (let i = 0; i < this._custom_views.length; i++) {
            this._custom_views[i].dispose();
        }
        this._custom_views.length = 0;
        this.viewer_scene.object_changed_observable.remove(this._object_changed_observer);
        this.viewer_scene.object_removed_observable.remove(this._object_removed_observer);
        this.asset_manager.dispose();
        this.light_manager.dispose();
        this._scene_bounds_changed_observable.dispose();
        this._objects_changed_observable.dispose();
        this._update_observable.dispose();
        this._default_view_before_render_observable.dispose();
        this._default_view_after_render_observable.dispose();
        this._default_view_camera_updated_observable.dispose();
        this._before_render_scene_setup_observable.dispose();
        this._click_void_observable.dispose();
        this._secondary_click_void_observable.dispose();
        this._viewer_resize_observable.dispose();
        this._viewer_dispose_observable.dispose();
        this.interactable_disposed_observable.dispose();
        this.viewer_scene.dispose();
        // Sometimes scene.dispose() gets stuck in an infinite loop when disposing transformNodes
        // This seems similar to what was posted about here: https://forum.babylonjs.com/t/scene-dispose-hangs/14178
        // We apply the same workaround here.
        let i = 0;
        while (i < this.scene.transformNodes.length) {
            const transform_node = this.scene.transformNodes[i];
            if (!transform_node.isDisposed()) {
                transform_node.dispose(true);
            }
            else {
                i++;
            }
        }
        this.scene.transformNodes = [];
        this.scene.dispose();
        this._engine.dispose();
        Logger.dispose();
        this._exception_handler.dispose();
        Viewer._viewer_instances_running--;
    }
    get_shadows_render_layer_mask() {
        return this._shadowmap_render_layer_mask;
    }
    set_shadows_render_layer_mask(mask) {
        const effective_mask = mask === null ? RENDER_LAYER_MASK_ALL : mask;
        if (effective_mask.bitmap === this._shadowmap_render_layer_mask.bitmap)
            return;
        this._shadowmap_render_layer_mask = { bitmap: effective_mask.bitmap };
        this.light_system.mark_all_shadowmaps_dirty();
    }
    get_hardware_limits() {
        return get_hardware_limits(this);
    }
    ao_supported() {
        return Framebuffer.is_depth_renderer_supported(this._engine) && Framebuffer.is_float_framebuffer_supported(this._engine);
    }
    set_temporal_ao_parameters(ao_settings) {
        assert(this._default_view !== null, 'Default view is not created');
        this._default_view.postprocess_manager.set_temporal_ao_parameters(ao_settings);
    }
    set_temporal_accumulation_frame_count(frame_count) {
        assert(this._default_view !== null, 'Default view is not created');
        this._default_view.postprocess_manager.set_temporal_accumulation_frame_count(frame_count);
    }
    enable_temporal_effects(use_ambient_occlusion = true) {
        assert(this._default_view !== null, 'Default view is not created');
        this._default_view.postprocess_manager.enable_temporal_effects(use_ambient_occlusion);
    }
    disable_temporal_effects() {
        assert(this._default_view !== null, 'Default view is not created');
        this._default_view.postprocess_manager.disable_temporal_effects();
    }
    get_canvas_render_width() {
        return this._canvas_render_width;
    }
    get_canvas_render_height() {
        return this._canvas_render_height;
    }
    unload_models(url) {
        let urls_to_check;
        if (typeof url === 'string') {
            urls_to_check = [];
            urls_to_check.push(url);
        }
        else {
            urls_to_check = url;
        }
        for (let i = 0; i < urls_to_check.length; i++) {
            const url = urls_to_check[i];
            this.viewer_scene.unload_model(url);
        }
    }
    get_world_position(object_id, offset) {
        const object = this.viewer_scene.objects.get(object_id);
        return object.transform_offset_with_world_matrix(offset);
    }
    get_world_direction_from_local_direction(object_id, local_direction) {
        const object = this.viewer_scene.objects.get(object_id);
        return object.transform_direction_with_world_matrix(local_direction);
    }
    set_alpha_sorting_index(object_id, alpha_index) {
        const object = this.viewer_scene.objects.get(object_id);
        object.alpha_index = alpha_index;
        this.mark_scene_dirty();
    }
    get_alpha_sorting_index(object_id) {
        const object = this.viewer_scene.objects.get(object_id);
        return object.alpha_index;
    }
    load_model(url, extension, model_transform) {
        const object = this.viewer_scene.load_model(url, extension, model_transform);
        return object.id;
    }
    load_model_async(url, extension, model_transform) {
        const object = this.viewer_scene.load_model(url, extension, model_transform);
        return object.ready.then(() => { return object.id; });
    }
    show_wireframe(object_id, enable) {
        const object = this.viewer_scene.objects.get(object_id);
        object.mesh_iterator(true, mesh => {
            if (mesh.material) {
                mesh.material.wireframe = enable;
            }
        });
        this.mark_scene_dirty();
    }
    persistent_load_models(url, extension, progress_callback) {
        if (progress_callback !== undefined) {
            Logger.warn("Deprecated parameter 'progress_callback' given to viewer.persistent_load_models. This will have no effect");
        }
        const promises = [];
        if (typeof url === 'string') {
            promises.push(this.viewer_scene.preload_model_and_mark_persistent(url, extension));
        }
        else {
            url.forEach((single_url) => {
                promises.push(this.viewer_scene.preload_model_and_mark_persistent(single_url, extension));
            });
        }
        return Promise.allSettled(promises).then(() => { });
    }
    is_persistent_url(url) {
        return this.viewer_scene.has_persistent_marking(url, undefined);
    }
    remove_persistent_url(url) {
        if (typeof url === 'string') {
            url = [url];
        }
        for (const file_path of url) {
            this.viewer_scene.remove_persistent_marking(file_path);
        }
    }
    load_material(url, extension) {
        if (this._loaded_material_map.has(url)) {
            return this._loaded_material_map.get(url);
        }
        const id = this.asset_manager.add_material();
        this._loaded_material_map.set(url, id);
        const destination = this.asset_manager.materials.get(id);
        destination._material_url = url;
        destination.ready = new Promise((resolve, reject) => {
            this.viewer_scene.preload_model_and_mark_persistent(url, extension).then((asset) => {
                if (asset.asset_container === null) {
                    reject(new Error('Failed to load any asset for material.'));
                    return;
                }
                const asset_materials = asset.asset_container.get_container_materials();
                if (asset_materials.length !== 0) {
                    const source_material = asset_materials[0];
                    const destination = this.asset_manager.materials.get(id);
                    if (destination === undefined) {
                        reject(new Error('Destination for material load was deleted.'));
                        return;
                    }
                    if (source_material instanceof ViewerMaterial) {
                        destination.material = this.asset_manager.get_or_create_material_wrapper(source_material.clone(''));
                    }
                    else if (source_material instanceof BetraktaPBRMaterialImplementation) {
                        destination.material = source_material.wrapper.clone();
                    }
                    else {
                        reject(new Error('Invalid material type in asset.'));
                    }
                    resolve();
                }
                else {
                    reject(new Error('Asset had no materials.'));
                }
            }).catch(reject);
        });
        return id;
    }
    object_ready(object_id) {
        return this.viewer_scene.objects.get(object_id).ready;
    }
    material_ready(material_id) {
        return this.asset_manager.materials.get(material_id).ready;
    }
    get_all_objects() {
        return this.viewer_scene.objects.ids();
    }
    get_all_objects_from_mask(mask) {
        return this.get_scene_objects_by_layer_mask(mask).map(obj => obj.id);
    }
    get_all_materials() {
        return this.asset_manager.materials.ids();
    }
    object_exists(object_id) {
        return this.viewer_scene.objects.contains(object_id);
    }
    material_exists(material_id) {
        return this.asset_manager.materials.contains(material_id);
    }
    ui_line_exists(line_id) {
        return this.line_manager.contains(line_id) || this._line_manager_on_top.contains(line_id);
    }
    recursive_object_iterator(object, f) {
        this.iterate_objects(object, true, (scene_object) => {
            f(scene_object.id);
        });
    }
    load_texture(url) {
        const t = new BABYLON_Texture(url, this.scene, false, false);
        t.onLoadObservable.addOnce(() => { this.mark_scene_dirty(); }); // When texture is loaded, mark frame as dirty.
        // TODO: Mark frame as dirty only if the texture is in use.
        return this.asset_manager.add_texture(t);
    }
    create_box(width, height, depth, material_id) {
        return this.mesh_manager.create_box(width, height, depth, material_id);
    }
    create_sphere(segments, diameter, material_id) {
        return this.mesh_manager.create_sphere(segments, diameter, material_id);
    }
    create_light_mesh(dimensions = { x: 1, y: 1, z: 1 }, cutoff = 0.5, flip_cutoff = true, segments = 8, material_id) {
        const object_id = this.mesh_manager.create_ellipsoid(segments, dimensions.x, dimensions.y, dimensions.z, cutoff, material_id);
        // Material needs to be set for the object to have a material id.
        const _material_id = (material_id === undefined) ? this.create_material() : material_id;
        this.set_material(object_id, _material_id);
        if (flip_cutoff) {
            // Since we slice (cutoff) the mesh, we want to make it face downwards rotating the mesh's transform node,
            // as opposed to the root node if the scene object.
            const object = this.viewer_scene.objects.get(object_id);
            object.mesh_iterator(false, (mesh) => {
                // We count on there only being one mesh. After all, we just created it.
                mesh.rotationQuaternion = BABYLON_Quaternion.FromLookDirectionRH(BABYLON_Vector3.RightHandedForwardReadOnly, BABYLON_Vector3.DownReadOnly);
            });
        }
        return object_id;
    }
    create_plane(width, height, uv0, uv1, material_id) {
        return this.mesh_manager.create_plane(width, height, uv0, uv1, material_id);
    }
    create_custom_mesh(description, material) {
        if (Array.isArray(description)) {
            if (material !== undefined) {
                throw new Error("Invalid use of create_custom_mesh, material is supplied when using mesh-material pairs array.");
            }
            return this.mesh_manager.create_custom_mesh(description).id;
        }
        else {
            if (material === undefined) {
                throw new Error("Invalid use of create_custom_mesh, undefined is not a valid material.");
            }
            return this.mesh_manager.create_custom_mesh([{ description: description, material: material }]).id;
        }
    }
    create_custom_lines(description) {
        return this.mesh_manager.create_custom_lines(description);
    }
    update_custom_lines(object, description) {
        this.mesh_manager.update_custom_lines(object, description);
    }
    get_all_custom_lines() {
        return [...this.asset_manager.lines];
    }
    remove_custom_lines(object) {
        object.dispose();
    }
    stop_and_remove_all_coroutines() {
        this._coroutine_manager.stop_and_remove_all_coroutines();
    }
    create_material(name) {
        return this.asset_manager.create_material(name);
    }
    get_material_from_material_id(material_id) {
        if (!this._optional_features.feature_gate_material_objects)
            throw Error('feature_gate_material_objects is not enabled');
        return this.asset_manager.materials.get(material_id).material;
    }
    get_scene_object_from_object_id(object_id) {
        return this.viewer_scene.objects.get(object_id);
    }
    create_empty_object() {
        return this.viewer_scene.create_object(null, 'empty', null).id;
    }
    create_text() {
        return new TextField(this, 100, 20);
    }
    create_reflection_probe(size = 64) {
        const probe = new ReflectionProbe(size, this);
        return probe;
    }
    get_material(id) {
        const object = this.viewer_scene.objects.get(id);
        const material = object.get_material();
        return material === null ? -1 : material;
    }
    get_roughness(id) { return this.asset_manager.materials.get(id).roughness; }
    set_roughness(id, roughness) {
        this.asset_manager.materials.get(id).roughness = roughness;
        this.mark_scene_dirty();
    }
    get_metallic(id) { return this.asset_manager.materials.get(id).metallic; }
    set_metallic(id, metallic) {
        this.asset_manager.materials.get(id).metallic = metallic;
        this.mark_scene_dirty();
    }
    get_base_color(id) { return this.asset_manager.materials.get(id).base_color; }
    set_base_color(id, color) {
        if (this.get_config().verbose_warnings && !IColor.is_saturated(color)) {
            Logger.warn('Setting color with one or more channel not in range 0-1, this is generally a bad idea');
        }
        this.asset_manager.materials.get(id).base_color = color;
        this.mark_scene_dirty();
    }
    set_emissive_color(id, color, intensity) {
        if (intensity !== undefined) {
            const GAMMA = 2.2;
            this.asset_manager.materials.get(id).emissive_color = {
                r: Math.pow(Math.pow(color.r, GAMMA), 1.0 / GAMMA),
                g: Math.pow(Math.pow(color.g, GAMMA), 1.0 / GAMMA),
                b: Math.pow(Math.pow(color.b, GAMMA), 1.0 / GAMMA)
            };
            this.asset_manager.materials.get(id).emissive_intensity = intensity;
        }
        else {
            this.asset_manager.materials.get(id).emissive_color = color;
        }
        this.mark_scene_dirty();
    }
    get_emissive_color(id) {
        return this.asset_manager.materials.get(id).emissive_color;
    }
    set_opacity(id, alpha) {
        const material_handle = this.asset_manager.materials.get(id);
        if (!material_handle) {
            throw new Error('Tried to set opacity of unknown material');
        }
        material_handle.ready.then(() => {
            material_handle.set_opacity(alpha);
            this.mark_scene_dirty();
        }, () => {
            Logger.warn('Material handle is not ready to set opacity.');
        });
    }
    get_base_color_texture(id) {
        return this.asset_manager.materials.get(id).base_color_texture;
    }
    set_base_color_texture(id, texture_id) {
        this.asset_manager.materials.get(id).base_color_texture = texture_id;
        this.mark_scene_dirty();
    }
    get_normal_texture(id) {
        return this.asset_manager.materials.get(id).normal_texture;
    }
    set_normal_texture(id, texture_id) {
        this.asset_manager.materials.get(id).normal_texture = texture_id;
        this.mark_scene_dirty();
    }
    get_occlusion_roughness_metallic_texture(id) {
        return this.asset_manager.materials.get(id).occlusion_roughness_metallic_texture;
    }
    set_occlusion_roughness_metallic_texture(id, texture_id) {
        this.asset_manager.materials.get(id).occlusion_roughness_metallic_texture = texture_id;
        this.mark_scene_dirty();
    }
    get_lightmap_texture(id) {
        return this.asset_manager.materials.get(id).lightmap_texture;
    }
    set_lightmap_texture(id, texture_id) {
        this.asset_manager.materials.get(id).lightmap_texture = texture_id;
        this.mark_scene_dirty();
    }
    get_unlit(material_id) {
        const babylon_material = this.asset_manager.materials.get(material_id);
        return babylon_material.unlit;
    }
    set_unlit(material_id, unlit) {
        const babylon_material = this.asset_manager.materials.get(material_id);
        babylon_material.unlit = unlit;
    }
    set_material(object_id, material_id) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.viewer_scene.objects.contains(object_id)) {
                return Promise.reject(new Error('Object does not exist'));
            }
            const object = this.viewer_scene.objects.get(object_id);
            const promise_material = object.set_material(material_id);
            if (object._asset_instance !== null) {
                yield Promise.all([promise_material, object.ready]);
            }
            else {
                return Promise.resolve();
            }
        });
    }
    delete_texture(id) {
        if (!this.asset_manager.textures.contains(id)) {
            Logger.warn(`Not able to remove texture with id: ${id}. The texture ID provided does not exist.`);
            return false;
        }
        const texture = this.asset_manager.textures.get(id);
        texture.dispose();
        return this.asset_manager.textures.remove(id);
    }
    is_material_used(id) {
        return this.asset_manager.is_material_used(id);
    }
    delete_material(material_id) {
        return this.asset_manager.delete_material(material_id);
    }
    on_object_removed(object) {
        this.remove_object_clip_plane(object.id);
        // If the object being disposed is currently picked and it has a parent with the same drag event, continue with the drag event as if it was the parent being dragged all along
        if (object === this._picked_object) {
            if (object.parent && object.parent.drag_interactable) {
                this._picked_object = object.parent;
            }
        }
        this.mark_world_bounds_dirty();
    }
    delete_object(object_id, delete_children = false) {
        if (!this.viewer_scene.objects.contains(object_id)) {
            Logger.warn(`Not able to remove scene object with id: ${object_id}. The object ID provided does not exist.`);
            return false;
        }
        const object = this.viewer_scene.objects.get(object_id);
        this.viewer_scene.delete_object(object, delete_children);
        return true;
    }
    // TODO: (partly) move implementation somewhere else? AssetManager?
    clone_object(object_id) {
        var _a, _b;
        const old_object = this.viewer_scene.objects.get(object_id);
        // The SceneAssetInstance.clone function will clone the model transform, so we make sure to send null as our own model transform
        const model_transform = null;
        const new_object = this.viewer_scene.create_object((_b = (_a = old_object._asset_instance) === null || _a === void 0 ? void 0 : _a.clone()) !== null && _b !== void 0 ? _b : null, 'clone of ' + old_object.name, model_transform);
        const mesh_url = old_object._mesh_url;
        new_object._mesh_url = mesh_url;
        new_object.set_position(old_object.position.x, old_object.position.y, old_object.position.z);
        new_object.set_rotation(old_object.rotation.x, old_object.rotation.y, old_object.rotation.z);
        new_object.set_scale(old_object.scale.x, old_object.scale.y, old_object.scale.z);
        new_object.visible = old_object.visible;
        new_object.receive_shadow = old_object.receive_shadow;
        new_object.enabled = old_object.enabled;
        new_object.outline_color = Object.assign({}, old_object.outline_color);
        new_object.outline_width = old_object.outline_width;
        new_object.set_outline_enabled(old_object.is_outline_enabled());
        new_object.set_outline_block_enabled(old_object.is_outline_block_enabled());
        new_object.set_render_layer_mask(old_object.get_render_layer_mask());
        return new_object.id;
    }
    replace_object(old_object_id, new_object_id, delete_old_object) {
        const old_object = this.viewer_scene.objects.get(old_object_id);
        const new_object = this.viewer_scene.objects.get(new_object_id);
        new_object.replace(old_object);
        if (delete_old_object) {
            this.delete_object(old_object_id, false); // After a replace it has no children anyway
        }
        this.mark_scene_dirty();
    }
    create_debug_layer() {
        var _a;
        if (this._debug_layer) {
            return this._debug_layer;
        }
        this._debug_layer = ((_a = this.get_debugger(false)) === null || _a === void 0 ? void 0 : _a.create_debug_layer()) || null;
        return this._debug_layer;
    }
    create_cutout_object(source) {
        return new CutoutObject(this, this.viewer_scene.objects.get(source));
    }
    create_cutout_shape(vertices, basis_x, basis_y) {
        return new CutoutShape(vertices, basis_x, basis_y);
    }
    set_position(object_id, x, y, z) {
        const object = this.viewer_scene.objects.get(object_id);
        object.set_position(x, y, z);
    }
    get_position(object_id) {
        return this.viewer_scene.objects.get(object_id).get_position().clone();
    }
    set_rotation(object_id, x, y, z) {
        const object = this.viewer_scene.objects.get(object_id);
        object.set_rotation(x, y, z);
    }
    get_rotation(object_id) {
        return this.viewer_scene.objects.get(object_id).get_rotation().clone();
    }
    set_scale(object_id, x, y, z) {
        const object = this.viewer_scene.objects.get(object_id);
        object.set_scale(x, y, z);
    }
    get_scale(object_id) {
        return this.viewer_scene.objects.get(object_id).get_scale().clone();
    }
    set_object_name(object_id, name) {
        const object = this.viewer_scene.objects.get(object_id);
        object.name = name;
    }
    get_object_name(object_id) {
        return this.viewer_scene.objects.get(object_id).name;
    }
    get_scene_bounds_changed_observable() {
        return this._scene_bounds_changed_observable;
    }
    get_object_transform_changed_observable(object_id) {
        if (!this._object_changed_observables.has(object_id)) {
            this._object_changed_observables.set(object_id, new Observable());
        }
        return this._object_changed_observables.get(object_id);
    }
    get_object_transform(object_id) {
        const object = this.viewer_scene.objects.get(object_id);
        const state = {
            world_transform: object.get_world_matrix().toIMatrix4ToRef(new IMatrix4()),
            local_transform: object.get_local_matrix().toIMatrix4ToRef(new IMatrix4())
        };
        return state;
    }
    get_objects_changed_observable() {
        return this._objects_changed_observable;
    }
    get_object_deleted_observable() {
        return this._object_deleted_observable;
    }
    get_specific_object_deleted_observable(object_id) {
        return this.viewer_scene.objects.get(object_id).disposed_observable;
    }
    get_update_observable() {
        return this._update_observable;
    }
    get_before_render_observable() {
        return this._default_view_before_render_observable;
    }
    get_after_render_observable() {
        return this._default_view_after_render_observable;
    }
    get_before_render_scene_setup_observable() {
        return this._before_render_scene_setup_observable;
    }
    get_object_drag_begin_observable(id) {
        return this.viewer_scene.objects.get(id).drag_begin_observable;
    }
    get_object_drag_observable(id) {
        return this.viewer_scene.objects.get(id).drag_observable;
    }
    get_object_drag_end_observable(id) {
        return this.viewer_scene.objects.get(id).drag_end_observable;
    }
    get_object_click_observable(id) {
        return this.viewer_scene.objects.get(id).click_observable;
    }
    get_object_secondary_click_observable(id) {
        return this.viewer_scene.objects.get(id).secondary_click_observable;
    }
    get_click_void_observable() {
        return this._click_void_observable;
    }
    get_secondary_click_void_observable() {
        return this._secondary_click_void_observable;
    }
    get_object_pointer_enter_observable(id) {
        return this.viewer_scene.objects.get(id).pointer_enter_observable;
    }
    get_object_pointer_leave_observable(id) {
        return this.viewer_scene.objects.get(id).pointer_leave_observable;
    }
    set_enable_on_drag(id, enable, propagate_to_children) {
        this.iterate_objects(id, propagate_to_children, (object) => {
            object.on_drag_enable = enable;
        });
    }
    get_enable_on_drag(object_id) {
        return this.viewer_scene.objects.get(object_id).on_drag_enable;
    }
    set_parent(child_id, parent_id) {
        const child = this.viewer_scene.objects.get(child_id);
        if (child.parent_id === parent_id)
            return;
        // Check for null and pass null if we want to unparent.
        const parent = parent_id !== null ? this.viewer_scene.objects.get(parent_id) : null;
        child.set_parent(parent);
        this.mark_scene_dirty();
    }
    get_parent(id) {
        const o = this.viewer_scene.objects.get(id);
        return o.parent_id;
    }
    get_children(id, direct_descendants_only = false, include_self = false) {
        const result = [];
        const start_object = this.viewer_scene.objects.get(id);
        if (direct_descendants_only) {
            if (include_self)
                result.push(id);
            start_object.children_direct_descendants_iterator((object) => { result.push(object.id); });
        }
        else {
            // All children recursive, include self only if requested
            start_object.children_iterator(true, include_self, (object) => { result.push(object.id); });
        }
        return result;
    }
    iterate_objects(id, recurse, func) {
        // We needed to make func optional since recurse was optional and we want to function as the last parameter
        if (!func)
            return;
        // If recurse is undefined it will not recurse (since it will be false)
        const start_object = this.viewer_scene.objects.get(id);
        start_object.children_iterator(!!recurse, true, (object) => { func(object); });
    }
    set_outline(id, enabled, propagate_to_children) {
        this.viewer_scene.objects.get(id).set_outline_enabled(enabled, propagate_to_children);
    }
    get_outline(id) {
        return this.viewer_scene.objects.get(id).is_outline_enabled();
    }
    set_outline_block(id, enabled, propagate_to_children) {
        this.viewer_scene.objects.get(id).set_outline_block_enabled(enabled, propagate_to_children);
    }
    get_outline_block(id) {
        return this.viewer_scene.objects.get(id).is_outline_block_enabled();
    }
    set_global_outline_color(color) {
        let babylon_color;
        if (color) {
            babylon_color = new BABYLON_Color4(color.r, color.g, color.b, IColor4.is(color) ? color.a : 1); // Set alpha to 1 if not IColor4
        }
        else {
            // undefined or null color means it will be reset to default color
        }
        if (this.get_config().verbose_warnings && babylon_color !== undefined && !IColor4.is_saturated(babylon_color)) {
            Logger.warn('Setting color with one or more channel not in range 0-1, this is generally a bad idea');
        }
        this.outline_layer.set_global_outline_color(babylon_color);
    }
    set_global_outline_width(width) {
        this.outline_layer.set_global_outline_width(width);
    }
    set_object_outline_color(id, color) {
        let outline_color = null;
        if (color !== null) {
            outline_color = IColor4.is(color) ? color : { r: color.r, g: color.g, b: color.b, a: 1 };
        }
        else {
            // A null color means we reset to default color
        }
        if (this.get_config().verbose_warnings && outline_color !== null && !IColor4.is_saturated(outline_color)) {
            Logger.warn('Setting color with one or more channel not in range 0-1, this is generally a bad idea');
        }
        this.viewer_scene.objects.get(id).outline_color = outline_color;
    }
    get_object_outline_color(id) {
        return this.viewer_scene.objects.get(id).outline_color;
    }
    set_object_outline_width(id, width) {
        this.viewer_scene.objects.get(id).outline_width = width;
    }
    get_object_outline_width(id) {
        return this.viewer_scene.objects.get(id).outline_width;
    }
    set_visible(id, visible, propagate_to_children) {
        // Separate code paths depending on if we need to iterate or just work on one object
        if (!propagate_to_children) {
            const object = this.viewer_scene.objects.get(id);
            if (object.visible !== visible) {
                // Only update and mark dirty in case we actually change anything
                object.visible = visible;
                this.mark_scene_dirty();
            }
        }
        else {
            let visibility_changed = false;
            this.iterate_objects(id, propagate_to_children, (object) => {
                if (object.visible !== visible) {
                    object.visible = visible;
                    visibility_changed = true;
                }
            });
            if (visibility_changed) {
                // Only update and mark dirty in case we actually change anything
                this.mark_scene_dirty();
            }
        }
    }
    get_visible(object_id) {
        return this.viewer_scene.objects.get(object_id).visible;
    }
    add_to_layer(object_id, layer, add_to_children) {
        if (add_to_children) {
            this.iterate_objects(object_id, true, (object) => {
                object.add_render_layer(layer);
            });
        }
        else {
            this.viewer_scene.objects.get(object_id).add_render_layer(layer);
        }
    }
    remove_from_layer(object_id, layer, remove_from_children) {
        if (remove_from_children) {
            this.iterate_objects(object_id, true, (object) => {
                object.remove_render_layer(layer);
            });
        }
        else {
            this.viewer_scene.objects.get(object_id).remove_render_layer(layer);
        }
    }
    is_part_of_layer(object_id, layer) {
        return this.viewer_scene.objects.get(object_id).has_render_layer(layer);
    }
    create_render_layer(allocation) {
        if (!this._layer_allocation_map.has(allocation)) {
            Logger.error('Invalid layer allocation');
            return null;
        }
        const available_bits = this._layer_allocation_map.get(allocation);
        if (available_bits.length === 0) {
            Logger.error("Layer allocation is used up, can't create a new layer.");
            return null;
        }
        const layer = {
            bit: available_bits.pop()
        };
        return layer;
    }
    set_focus_border(id, focus) {
        if (focus) {
            if (this._object_focus_borders.has(id))
                return;
            this._object_focus_borders.set(id, new FocusBorder(this.viewer_scene.objects.get(id), this));
        }
        else {
            // Remove focus border
            const focus_border = this._object_focus_borders.get(id);
            if (focus_border) {
                focus_border.dispose();
            }
            this._object_focus_borders.delete(id);
        }
    }
    get_focus_border(id) {
        return this._object_focus_borders.has(id);
    }
    set_enabled(id, enabled) {
        this.viewer_scene.objects.get(id).enabled = enabled;
        this.mark_scene_dirty();
    }
    get_enabled(object_id) {
        return this.viewer_scene.objects.get(object_id).enabled;
    }
    set_enable_on_click(id, enable, propagate_to_children) {
        this.iterate_objects(id, propagate_to_children, (object) => {
            object.on_click_enable = enable;
        });
    }
    get_enable_on_click(object_id) {
        return this.viewer_scene.objects.get(object_id).on_click_enable;
    }
    get_active_camera() {
        assert(this._default_view !== null, 'Default view is not created');
        return this._default_view.camera_manager.active_camera;
    }
    push_camera(viewer_camera, transition = CameraTransitionPreset.NONE) {
        assert(this._default_view !== null, 'Default view is not created');
        if (transition === CameraTransitionPreset.NONE) {
            this._default_view.camera_manager.push_camera_immediate(viewer_camera);
        }
        else {
            this._default_view.camera_manager.push_camera(viewer_camera, TRANSITION_PRESETS[transition]);
        }
    }
    pop_camera(transition = CameraTransitionPreset.NONE) {
        assert(this._default_view !== null, 'Default view is not created');
        if (transition === CameraTransitionPreset.NONE) {
            this._default_view.camera_manager.pop_camera_immediate();
        }
        else {
            this._default_view.camera_manager.pop_camera(TRANSITION_PRESETS[transition]);
        }
    }
    replace_camera(viewer_camera, transition = CameraTransitionPreset.NONE) {
        assert(this._default_view !== null, 'Default view is not created');
        if (transition === CameraTransitionPreset.NONE) {
            this._default_view.camera_manager.replace_camera_immediate(viewer_camera);
        }
        else {
            this._default_view.camera_manager.replace_camera(viewer_camera, TRANSITION_PRESETS[transition]);
        }
    }
    get_camera_transition_begin_observable() {
        return this._default_view_camera_transition_begin_observable;
    }
    get_camera_transition_end_observable() {
        return this._default_view_camera_transition_end_observable;
    }
    set_camera_input_enabled(value) {
        assert(this._default_view !== null, 'Default view is not created');
        this._default_view.input_manager.camera_input_enabled = value;
    }
    get_camera_input_enabled() {
        assert(this._default_view !== null, 'Default view is not created');
        return this._default_view.input_manager.camera_input_enabled;
    }
    get_active_camera_changed_observable() {
        return this._default_view_active_camera_changed_observable;
    }
    get_camera_updated_observable() {
        return this._default_view_camera_updated_observable;
    }
    get_camera_state() {
        assert(this._default_view !== null, 'Default view is not created');
        return this._default_view.engine_camera;
    }
    // -------- Lights --------
    create_light(type, ...args) {
        return this._light_manager.create_light(type, ...args);
    }
    can_create_light() {
        return this._light_manager.can_create_light();
    }
    delete_light(light) {
        return this._light_manager.delete_light(light);
    }
    get_all_lights() {
        return this._light_manager.light_system.get_all_lights();
    }
    clear_lights() {
        this._light_manager.remove_all_lights();
    }
    debug_light_visualizer_color_mode(mode) {
        this.light_system.debug_color_mode = mode;
    }
    is_ambient_light_enabled() {
        return this.light_manager.ambient_light.is_enabled();
    }
    set_ambient_light_enabled(v) {
        this.light_manager.ambient_light.set_enabled(v);
    }
    set_ambient_light_intensity(intensity) {
        this.light_manager.ambient_light.set_intensity(intensity);
    }
    set_ambient_light_diffuse_color(color) {
        if (this.get_config().verbose_warnings && !IColor.is_saturated(color)) {
            Logger.warn('Setting color with one or more channel not in range 0-1, this is generally a bad idea');
        }
        this.light_manager.ambient_light.set_color(color);
    }
    set_ambient_light_ground_color(color) {
        if (this.get_config().verbose_warnings && !IColor.is_saturated(color)) {
            Logger.warn('Setting color with one or more channel not in range 0-1, this is generally a bad idea');
        }
        this.light_manager.ambient_light.set_ground_color(color);
    }
    set_environment_map(url, file_extension) {
        if (url === null) {
            return this.light_manager.environment.set_environment_map(null, null, false);
        }
        const extension = get_file_extension(url, file_extension);
        if (extension !== ".env" && extension !== '.dds') {
            Logger.warn('Loading an environment map that is not a DDS or ENV file, this usage is deprecated.');
        }
        return this.light_manager.environment.set_environment_map(url, file_extension !== null && file_extension !== void 0 ? file_extension : null, false);
    }
    load_and_filter_environment_map(url, file_extension) {
        const extension = (file_extension !== null && file_extension !== void 0 ? file_extension : url).toLowerCase();
        if (!extension.endsWith('hdr')) {
            return Promise.reject(new Error('Invalid format for environment map. Must be .hdr'));
        }
        return this.light_manager.environment.set_environment_map(url, file_extension !== null && file_extension !== void 0 ? file_extension : null, true);
    }
    get_environment_map() {
        return this.light_manager.environment.get_environment_map();
    }
    set_environment_map_local_mode(settings) {
        this.light_manager.environment.set_environment_map_local_mode(settings);
    }
    get_environment_map_local_mode() {
        return this.light_manager.environment.get_environment_map_local_mode();
    }
    set_environment_map_intensity(intensity) {
        this.light_manager.environment.set_environment_map_intensity(intensity);
    }
    set_environment_map_rotation_radians(rotation) {
        this.light_manager.environment.set_environment_map_rotation(rotation);
    }
    set_environment_map_rotation_degrees(rotation) {
        this.light_manager.environment.set_environment_map_rotation(radians(rotation));
    }
    get_environment_map_intensity() {
        return this.light_manager.environment.get_environment_map_intensity();
    }
    get_environment_map_rotation_radians() {
        return this.light_manager.environment.get_environment_map_rotation();
    }
    get_environment_map_rotation_degrees() {
        return degrees(this.light_manager.environment.get_environment_map_rotation());
    }
    get_environment_map_file_type() {
        return this.light_manager.environment.get_environment_map_file_type();
    }
    get_receive_shadow(id) {
        const object = this.viewer_scene.objects.get(id);
        return object.receive_shadow;
    }
    set_receive_shadow(id, receive_shadow, propagate_to_children) {
        this.iterate_objects(id, propagate_to_children, (object) => {
            object.receive_shadow = receive_shadow;
            if (object.material !== null) {
                const material = this.asset_manager.materials.get(object.material);
                material.set_dirty();
            }
        });
        this.freezing_manager.set_all_materials_dirty();
        this.mark_scene_dirty();
    }
    set_cast_shadow(id, cast_shadow, propagate_to_children) {
        if (this.viewer_scene.objects.contains(id)) { // check so that the user did not delete the object in the meanwhile
            this.iterate_objects(id, propagate_to_children, (object) => {
                if (cast_shadow) {
                    object.add_render_layer(RENDER_LAYER_SHADOW);
                }
                else {
                    object.remove_render_layer(RENDER_LAYER_SHADOW);
                }
            });
        }
    }
    get_cast_shadow(id) {
        return this.viewer_scene.objects.get(id).has_render_layer(RENDER_LAYER_SHADOW);
    }
    // 0 If no render
    debug_get_fps() {
        return this._last_frame_was_composited ? this._engine.getFps() : 0;
    }
    debug_get_updated_per_second() {
        return this._engine.getFps();
    }
    resize(render_width, render_height) {
        var _a, _b;
        if (render_width === undefined || render_height === undefined) {
            if (this.get_config().verbose_warnings) {
                Logger.warn('Using viewer.resize without width & height parameters is deprecated. Use canvas.clientWidth and canvas.clientHeight for previous behaviour.');
            }
        }
        else {
            if (isNaN(render_width) || isNaN(render_height)) {
                throw new Error('Viewer.resize width and height need to be numbers.');
            }
            render_width = Math.floor(render_width);
            render_height = Math.floor(render_height);
            if (render_width <= 0 || render_height <= 0) {
                throw new Error('Viewer.resize width and height need to be greater than zero.');
            }
            if (render_width > MAX_RENDER_SIZE || render_height > MAX_RENDER_SIZE) {
                throw new Error(`Viewer.resize width and height cannot exceed ${MAX_RENDER_SIZE}.`);
            }
        }
        render_width = render_width !== null && render_width !== void 0 ? render_width : this.canvas_element.clientWidth;
        render_height = render_height !== null && render_height !== void 0 ? render_height : this.canvas_element.clientHeight;
        this.canvas_element.width = render_width;
        this.canvas_element.height = render_height;
        this._canvas_render_width = render_width;
        this._canvas_render_height = render_height;
        (_a = this._default_view) === null || _a === void 0 ? void 0 : _a.set_render_size(render_width, render_height);
        (_b = this._default_view) === null || _b === void 0 ? void 0 : _b.set_canvas_viewport({ x: 0, y: 0, width: render_width, height: render_height });
        this.mark_scene_dirty();
        this.mark_backbuffer_clear_dirty();
        this._viewer_resize_observable.notify(this, { width: render_width, height: render_height });
    }
    add_bake_job_async(job) {
        return __awaiter(this, void 0, void 0, function* () {
            const object_ready_promises = job.targets.map((id) => this.object_ready(id));
            yield Promise.all(object_ready_promises);
            if (this._baker === null) {
                this._baker = new LightBaker(this, job);
            }
            else {
                this._baker.reconfigure(job);
            }
            const lightmap_texture = yield this._baker.async_bake();
            return this.asset_manager.add_texture(lightmap_texture);
        });
    }
    set_object_bounds_for_interaction(object_id, bounds) {
        this.viewer_scene.objects.get(object_id).set_raycast_bounds(bounds);
    }
    get_default_view_canvas_ray(x, y) {
        assert(this._default_view, 'get_default_view_canvas_ray requires the defualt view');
        // Normalize canvas coordinates to default view
        const ray = BABYLON_Ray.Zero();
        const view_viewport = this._default_view.get_canvas_viewport();
        this._default_view.create_ray_to_ref(clamp((x - view_viewport.x) / view_viewport.width, 0.0, 1.0), clamp((y - view_viewport.y) / view_viewport.height, 0.0, 1.0), ray);
        return ray;
    }
    raycast_object_using_canvas_coordinate(x, y, filter) {
        assert(this._default_view, 'raycast_object_using_canvas_coordinate requires the defualt view');
        return this.raycast_object_using_ray(this.get_default_view_canvas_ray(x, y), filter);
    }
    raycast_with_ray_and_filter_internal(func, origin_or_ray, direction_or_filter, length, filter) {
        const ray = BABYLON_Ray.Zero();
        if (direction_or_filter && 'x' in direction_or_filter) {
            // direction_or_filter is IVector3
            update_babylon_ray(ray, origin_or_ray, direction_or_filter, length);
        }
        else if ('origin' in ray && 'direction' in ray) { // TODO: Make IRay.is() type guard when we move to static classes
            // origin_or_ray is a ray
            update_babylon_ray(ray, origin_or_ray, undefined, length);
        }
        else {
            throw Error('Bad parameter combination for raycast_with_ray_and_filter_internal');
        }
        let hits = func(this, ray);
        if (Array.isArray(direction_or_filter)) {
            filter = direction_or_filter;
        }
        if (filter) {
            hits = hits.filter((raycast_result) => filter === null || filter === void 0 ? void 0 : filter.includes(raycast_result.object));
        }
        return filter_closest_hit(hits);
    }
    raycast_object_using_ray(origin_or_ray, direction_or_filter, length, filter) {
        return this.raycast_with_ray_and_filter_internal(raycast_all_objects_internal, origin_or_ray, direction_or_filter, length, filter);
    }
    raycast_gizmo_using_canvas_coordinate(x, y, filter) {
        assert(this._default_view, 'raycast_gizmo_using_canvas_coordinate requires the defualt view');
        return this.raycast_gizmo_using_ray(this.get_default_view_canvas_ray(x, y), filter);
    }
    raycast_gizmo_using_ray(origin_or_ray, direction_or_filter, length, filter) {
        return this.raycast_with_ray_and_filter_internal(raycast_all_gizmos_internal, origin_or_ray, direction_or_filter, length, filter);
    }
    raycast_interactable_using_canvas_coordinate(x, y, filter) {
        assert(this._default_view, 'raycast_interactable_using_canvas_coordinate requires the defualt view');
        return this.raycast_interactable_using_ray(this.get_default_view_canvas_ray(x, y), filter);
    }
    raycast_interactable_using_ray(origin_or_ray, direction_or_filter, length, filter) {
        return this.raycast_with_ray_and_filter_internal(raycast_all_internal, origin_or_ray, direction_or_filter, length, filter);
    }
    raycast_all_using_ray(origin_or_ray, direction, length) {
        const ray = BABYLON_Ray.Zero();
        update_babylon_ray(ray, origin_or_ray, direction, length);
        const hits = raycast_all_internal(this, ray);
        filter_hits_to_one_per_interactable_in_place(hits);
        return hits;
    }
    get_object_bounds_world_sync(object_id) {
        const object = this.viewer_scene.objects.get(object_id);
        assert(object.is_loaded, 'Object needs to have finished loading before getting bounds sync');
        const boundingbox = {
            min: { x: Number.POSITIVE_INFINITY, y: Number.POSITIVE_INFINITY, z: Number.POSITIVE_INFINITY },
            max: { x: Number.NEGATIVE_INFINITY, y: Number.NEGATIVE_INFINITY, z: Number.NEGATIVE_INFINITY }
        };
        update_aabb_by_transformed_aabb(boundingbox, object.get_local_bounds_sync().aabb, object.get_world_matrix());
        return boundingbox;
    }
    object_is_loaded(object_id) {
        return this.viewer_scene.objects.get(object_id).is_loaded;
    }
    object_is_empty(object_id) {
        return this.viewer_scene.objects.get(object_id).is_empty;
    }
    project_world_position_to_canvas(x, y, z) {
        assert(this._default_view !== null, 'Default view is not created');
        return this._default_view.project_world_position_to_canvas({ x: x, y: y, z: z });
    }
    // TODO: Make generic utility function?
    static verify_screenshot_mime_type(screenshot_data, expected_type) {
        // No need to check if MIME type isn't provided
        if (!expected_type) {
            return;
        }
        // Try to fetch and compare actual MIME type with expected
        const re_array = screenshot_data.match(/data:(image\/\w+)/);
        if (re_array && re_array.length > 1 && re_array[1] !== expected_type) {
            Logger.warn(`Created image has an unexpected MIME type. Expected: ${expected_type}. Actual: ${re_array[1]}.`);
        }
    }
    set_background_color(r, g, b) {
        if (this.get_config().verbose_warnings && !IColor.is_saturated({ r: r, g: g, b: b })) {
            Logger.warn('Setting color with one or more channel not in range 0-1, this is generally a bad idea');
        }
        if (this._background_color.r === r && this._background_color.g === g && this._background_color.b === b) {
            return;
        }
        this._background_color.r = r;
        this._background_color.g = g;
        this._background_color.b = b;
        if (this._float_framebuffer_enabled) {
            const inverse_tonemapped_color = get_fitted_inverse_tonemap(VIEWER_DEFAULT_FLOAT_FRAMEBUFFER_TYPE, r, g, b, this.get_config().verbose_warnings);
            const inverse_color_rrt_sat = srgb_to_rrt_sat(inverse_tonemapped_color);
            this.scene.clearColor = new BABYLON_Color4(inverse_color_rrt_sat.x, inverse_color_rrt_sat.y, inverse_color_rrt_sat.z, 1.0);
        }
        else {
            this.scene.clearColor = new BABYLON_Color4(r, g, b, 1.0);
        }
        this.mark_backbuffer_clear_dirty();
        this.mark_scene_dirty();
    }
    get_background_color() {
        return {
            r: this._background_color.r,
            g: this._background_color.g,
            b: this._background_color.b
        };
    }
    mark_scene_dirty() {
        this._scene_dirty = true;
    }
    mark_composite_dirty() {
        this._compositing_frames_remaining = FRAMES_TO_COMPOSITE;
    }
    mark_backbuffer_clear_dirty() {
        this._backbuffer_clear_dirty = true;
        this.mark_composite_dirty();
    }
    mark_world_bounds_dirty() {
        this._world_bounds_is_dirty = true;
    }
    create_ui_line(type, p0, p1, color, thickness, draw_on_top = false) {
        if (draw_on_top) {
            return this._line_manager_on_top.create_line(type, vector_3(p0), vector_3(p1), icolor_to_col4(color), thickness);
        }
        else {
            return this.line_manager.create_line(type, vector_3(p0), vector_3(p1), icolor_to_col4(color), thickness);
        }
    }
    update_ui_line(id, p0, p1, color, thickness) {
        if (this.line_manager.contains(id)) {
            this.line_manager.update_line(id, p0 ? vector_3(p0) : p0, p1 ? vector_3(p1) : p1, color ? icolor_to_col4(color) : color, thickness);
        }
        else {
            this._line_manager_on_top.update_line(id, p0 ? vector_3(p0) : p0, p1 ? vector_3(p1) : p1, color ? icolor_to_col4(color) : color, thickness);
        }
    }
    delete_ui_line(id) {
        const line_exists = this.line_manager.contains(id) || this._line_manager_on_top.contains(id);
        if (!line_exists && this.get_config().verbose_warnings) {
            Logger.warn(`Not able to remove ui line with id: ${id}. The line ID provided does not exist.`);
        }
        return this.line_manager.remove_line(id) || this._line_manager_on_top.remove_line(id);
    }
    set_ui_line_visible(id, line_visible) {
        if (this.line_manager.contains(id)) {
            this.line_manager.set_visible(id, line_visible);
        }
        else {
            this._line_manager_on_top.set_visible(id, line_visible);
        }
    }
    get_ui_line_visible(id) {
        // Lines that don't exists return false, so we just try if its in either set, otherwise both are false anyway.
        return this.line_manager.get_visible(id) || this._line_manager_on_top.get_visible(id);
    }
    on_current_scene_ready() {
        // Get all object ready promises
        // Get all material ready promises
        // Get all lights ready promises
        // Check environment ready
        // TODO: How do we handle things like proxy loader? guess we don't.
        return new Promise((resolve) => {
            const promises = [];
            this.viewer_scene.objects.forEach((scene_object) => promises.push(scene_object.ready));
            this.asset_manager.materials.forEach((material) => promises.push(material.ready));
            promises.push(this.light_system.ready);
            promises.push(this.light_manager.environment.ready);
            promises.push(this.get_effects_compiled_promise());
            Promise.allSettled(promises).then(() => resolve());
        });
    }
    set_object_base_color_factor(id, base_color) {
        if (this.get_config().verbose_warnings && !IColor.is_saturated(base_color)) {
            Logger.warn('Setting color with one or more channel not in range 0-1, this is generally a bad idea');
        }
        const propagate_to_children = false;
        this.iterate_objects(id, propagate_to_children, (object) => {
            object.set_base_color(base_color);
        });
        this.mark_scene_dirty();
    }
    set_object_roughness_factor(id, roughness_factor) {
        const propagate_to_children = false;
        this.iterate_objects(id, propagate_to_children, (object) => {
            object.set_roughness_factor(roughness_factor);
        });
        this.mark_scene_dirty();
    }
    get_object_bounds_world(...ids) {
        const objects = ids.map(id => this.viewer_scene.objects.get(id));
        return get_object_bounds_world(objects);
    }
    calculate_current_world_bounds() {
        const world_bounds = IBoundingBox.Invalid();
        const tmp_matrix = new IMatrix4();
        this.viewer_scene.objects.forEach((object) => {
            if (!object.has_bounds || object.is_empty || !object.enabled)
                return;
            const local_bounds = object.get_local_bounds_sync();
            if (!IBoundingBox.is_valid(local_bounds.aabb))
                return;
            const transformed_bounds = new ITransformedBoundingBox(local_bounds.aabb, object.get_world_matrix().toIMatrix4ToRef(tmp_matrix));
            const object_world_bounds = ITransformedBoundingBox.get_encapsulating_axis_aligned_bounding_box(transformed_bounds);
            IBoundingBox.encapsulate_in_place(world_bounds, object_world_bounds.min, object_world_bounds.max);
        });
        this.asset_manager.ui_meshes.forEach((instance) => {
            const ui_mesh_aabb_transformed = instance.get_world_bounds();
            if (!IBoundingBox.is_valid(ui_mesh_aabb_transformed.local_bounding_box))
                return;
            const ui_mesh_aabb_world = ITransformedBoundingBox.get_encapsulating_axis_aligned_bounding_box(ui_mesh_aabb_transformed);
            IBoundingBox.encapsulate_in_place(world_bounds, ui_mesh_aabb_world.min, ui_mesh_aabb_world.max);
        });
        this.asset_manager.lines.forEach((lines) => {
            const aabb_transformed = lines.get_world_bounds();
            if (!IBoundingBox.is_valid(aabb_transformed.local_bounding_box))
                return;
            const aabb_world = ITransformedBoundingBox.get_encapsulating_axis_aligned_bounding_box(aabb_transformed);
            IBoundingBox.encapsulate_in_place(world_bounds, aabb_world.min, aabb_world.max);
        });
        this.asset_manager.text_fields.forEach((text_field) => {
            text_field.get_meshes().forEach((mesh) => {
                const text_field_aabb = mesh.getBoundingInfo().boundingBox;
                IBoundingBox.encapsulate_in_place(world_bounds, text_field_aabb.minimumWorld, text_field_aabb.maximumWorld);
            });
        });
        const ui_lines_bounds = this.line_manager.get_bounds();
        if (IBoundingBox.is_valid(ui_lines_bounds)) {
            IBoundingBox.encapsulate_in_place(world_bounds, ui_lines_bounds.min, ui_lines_bounds.max);
        }
        const ui_line_on_top_bounds = this._line_manager_on_top.get_mesh().getBoundingInfo().boundingBox;
        if (is_valid_bounding_box(ui_line_on_top_bounds)) {
            IBoundingBox.encapsulate_in_place(world_bounds, ui_line_on_top_bounds.minimumWorld, ui_line_on_top_bounds.maximumWorld);
        }
        return world_bounds;
    }
    get_object_bounds_local(id, include_child_objects = true) {
        const object = this.viewer_scene.objects.get(id);
        return get_object_bounds_local(object, include_child_objects);
    }
    update_bounds_cache() {
        if (this._world_bounds_is_dirty) {
            this._world_bounds_is_dirty = false;
            const world_bounds = this.calculate_current_world_bounds();
            // Setting flag here to avoid notifying multiple times per frame
            if (!IBoundingBox.equals(this._scene_bounds, world_bounds)) {
                this._should_notify_world_bounds = true;
                this._scene_bounds = world_bounds;
            }
        }
    }
    get_scene_bounds() {
        this.update_bounds_cache();
        return this._scene_bounds;
    }
    download_gltf_files(files) {
        download_gltf_files(files);
    }
    serialize_glb(filter, filename, center) {
        const objects = filter.map((id) => this.viewer_scene.objects.get(id));
        return this.asset_manager.serialize_glb(objects, filename, center);
    }
    create_animation(object, data) {
        return this.animation_system.create_animation(object, data);
    }
    start_animation(id, animation_idx, from, to) {
        this.animation_system.start_animation(id, animation_idx, from, to);
    }
    stop_animation(id, animation_idx) {
        return this.animation_system.stop_animation(id, animation_idx);
    }
    animation_running(id, animation_idx) {
        return this.animation_system.animation_running(id, animation_idx);
    }
    get_animations(object) {
        return this.animation_system.get_animations(object);
    }
    debug_download_texture(id) {
        debug_download_texture(this.asset_manager.textures.get(id));
    }
    set_object_clip_plane(object_id, plane) {
        const object = this.viewer_scene.objects.get(object_id);
        object.clip_plane = plane;
    }
    remove_object_clip_plane(object_id) {
        var _a;
        const object = this.viewer_scene.objects.get(object_id);
        (_a = object._asset_instance) === null || _a === void 0 ? void 0 : _a.instance_iterator((instance) => {
            instance.set_clip_plane(null);
        });
    }
    set_bloom(enable, intensity) {
        if (enable && !this.is_bloom_supported()) {
            Logger.warn('Bloom is not supported on this viewer instance. Requires WebGL2 and float render targets.');
            return;
        }
        this._default_view.postprocess_manager.set_bloom(enable, intensity);
    }
    is_bloom_supported() {
        // Bloom requires a float render target, both for its own rendering, but also for its HDR input.
        // The bloom shaders also required WebGL 2 (for texelFetch), which is implicitly required for float render targets.
        return this._config.use_hdr_framebuffer && Framebuffer.is_float_framebuffer_supported(this._engine);
    }
    get_input_manager() {
        assert(this._default_view !== null, 'Default view is not created');
        return this._default_view.input_manager;
    }
    // Note that this returns null without the feature_gate_enable_input_manager
    get_camera_manager() {
        assert(this._default_view !== null, 'Default view is not created');
        return this._default_view.camera_manager;
    }
    get_asset_manager() {
        return this.asset_manager;
    }
    get_mesh_manager() {
        return this.mesh_manager;
    }
    get_viewer_resize_observable() {
        return this._viewer_resize_observable;
    }
    build_metadata_structure(object_id, namespace) {
        const namespace_map = get_or_set_map_value(this._object_metadata_store, object_id, new Map());
        const metadata_map = get_or_set_map_value(namespace_map, namespace, new Map());
        return metadata_map;
    }
    add_metadata(object_id, key, value, namespace) {
        // Early out if object_id is not a valid object
        if (!this.viewer_scene.objects.contains(object_id))
            return false;
        if (namespace === undefined) {
            Logger.error('metadata without namespace is currently not implemented');
        }
        else {
            // Get or create all the maps for the metadata storage
            const metadata_map = this.build_metadata_structure(object_id, namespace);
            // If we dont have any entry for key, we add it and return true
            if (!metadata_map.has(key)) {
                metadata_map.set(key, value);
                return true;
            }
        }
        return false;
    }
    update_metadata(object_id, key, value, namespace) {
        // Early out if object_id is not a valid object
        if (!this.viewer_scene.objects.contains(object_id))
            return false;
        if (namespace === undefined) {
            Logger.error('metadata without namespace is currently not implemented');
        }
        else {
            // Get or create all the maps for the metadata storage
            const metadata_map = this.build_metadata_structure(object_id, namespace);
            if (metadata_map.has(key)) {
                metadata_map.set(key, value);
                return true;
            }
        }
        return false;
    }
    get_metadata(object_id, key, namespace) {
        // Early out if object_id is not a valid object
        if (!this.viewer_scene.objects.contains(object_id))
            return undefined;
        if (namespace === undefined) {
            Logger.error('metadata without namespace is currently not implemented');
        }
        else {
            const metadata_map = this.build_metadata_structure(object_id, namespace);
            return metadata_map.get(key);
        }
        return undefined;
    }
    remove_metadata(object_id, key, namespace) {
        // Early out if object_id is not a valid object
        if (!this.viewer_scene.objects.contains(object_id))
            return false;
        if (namespace === undefined) {
            Logger.error('metadata without namespace is currently not implemented');
        }
        else {
            const metadata_map = this.build_metadata_structure(object_id, namespace);
            return metadata_map.delete(key);
        }
        return false;
    }
    set_canvas_tab_index(tab_index) {
        this._engine.canvasTabIndex = tab_index;
    }
    get_canvas_tab_index() {
        return this._engine.canvasTabIndex;
    }
    get_viewer_dispose_observable() {
        return this._viewer_dispose_observable;
    }
    start_coroutine(coroutine) {
        this._coroutine_manager.start_coroutine(coroutine);
    }
    get_version() {
        return VIEWER_VERSION;
    }
    get_debugger(display_help) {
        Logger.error('To use the debugger you must import "@inter-ikea-betrakta/viewer/dist/babylon/debug/ViewerDebugger"');
        return null;
    }
    get_interaction_system() {
        return this._interaction_system;
    }
    pause_rendering() {
        this._rendering_is_paused = true;
    }
    resume_rendering() {
        this._rendering_is_paused = false;
    }
    acquire_rendering_pause_lock() {
        return this._rendering_lock_system.acquire_lock();
    }
    release_rendering_pause_lock(lock) {
        this._rendering_lock_system.release_lock(lock);
    }
    is_rendering_paused() {
        return this._rendering_is_paused || this._rendering_lock_system.is_locked();
    }
    force_render_frame(number_of_frames) {
        this._forced_render_frame_count = number_of_frames;
    }
    is_rendering_to_float_framebuffer() {
        return this._float_framebuffer_enabled;
    }
    get_scene_objects_by_layer_mask(mask) {
        // This is just filtering the objects
        const objects = [];
        this.viewer_scene.objects.forEach((object) => {
            // Check for overlap of the object's mask and the mask we want to fetch
            if ((mask.bitmap & object.get_render_layer_mask().bitmap) !== 0) {
                objects.push(object);
            }
        });
        return objects;
    }
    get_babylon_meshes_from_objects(scene_objects) {
        const meshes = [];
        for (let i = 0; i < scene_objects.length; i++) {
            scene_objects[i].mesh_iterator(false, (mesh) => meshes.push(mesh));
        }
        return meshes;
    }
    /**
     * Returns an array of the Babylon meshes from scene objects and UI lines that have render layers included in the mask.
     * This function is meant to be used for rendering a special list of meshes, such as for screenshots.
     * Since UI lines (and possibly other items later down the road) are not scene objects, it's not enough to just provide the list of scene objects that the mask matches.
     * @param mask The mask to match for
     * @returns The Babylon meshes
     */
    get_meshes_by_layer_mask(mask) {
        // Fetch all scene objects that are included in our mask
        const scene_objects = this.get_scene_objects_by_layer_mask(mask);
        // Get the babylon meshes for all of those objects
        const meshes = this.get_babylon_meshes_from_objects(scene_objects);
        // Special case: Since UI lines are not part of the scene objects, we add them manually if their render layer is active.
        if (RenderLayerMask.has_layer(mask, this.line_manager.render_layer)) {
            meshes.push(this.line_manager.get_mesh());
        }
        if (RenderLayerMask.has_layer(mask, this._line_manager_on_top.render_layer)) {
            meshes.push(this._line_manager_on_top.get_mesh());
        }
        // Special case: TextFields are not scene objects so we need to handle them here
        this.asset_manager.text_fields.forEach((text_field) => {
            if ((text_field.get_render_layer_mask().bitmap & mask.bitmap) !== 0) {
                text_field.get_meshes().forEach(mesh => {
                    meshes.push(mesh);
                });
            }
        });
        // Special case: ISceneLines
        this.asset_manager.lines.forEach((lines) => {
            if ((lines.render_layer_mask.bitmap & mask.bitmap) !== 0) {
                meshes.push(lines.mesh);
            }
        });
        // Special case: UiMeshInstances
        this.asset_manager.ui_meshes.forEach((ui_mesh) => {
            ui_mesh.instances.forEach((mesh) => {
                if ((mesh.layerMask & mask.bitmap) !== 0) {
                    meshes.push(mesh);
                }
            });
        });
        // Special case: Skybox
        const skybox = this.light_manager.environment.get_skybox();
        if (skybox !== null) {
            if ((skybox.layerMask & mask.bitmap) !== 0) {
                meshes.push(skybox);
            }
        }
        return meshes;
    }
    take_screenshot(projection_matrix, projection_type, view_matrix, width, height, settings, layer_mask = RENDER_LAYER_MASK_DEFAULT) {
        return take_screenshot(this, projection_matrix, projection_type, view_matrix, width, height, settings, this.get_meshes_by_layer_mask(layer_mask));
    }
    take_edge_sketch_screenshot(projection_matrix, projection_type, view_matrix, width, height, settings, layer_mask = RENDER_LAYER_MASK_DEFAULT) {
        const experimental_settings = {
            edge_sketch: true
        };
        return take_screenshot(this, projection_matrix, projection_type, view_matrix, width, height, settings, this.get_meshes_by_layer_mask(layer_mask), experimental_settings);
    }
    get_object_bounds_plane_world(object_id, direction) {
        const object = this.viewer_scene.objects.get(object_id);
        const world_transform = object.get_world_matrix();
        return object.get_local_bounds().then((bounds) => {
            return ITransformedBoundingBox.get_face_plane(new ITransformedBoundingBox(bounds.aabb, world_transform.toIMatrix4ToRef(new IMatrix4())), direction);
        });
    }
    get_object_bounds_plane_local(object_id, direction) {
        const object = this.viewer_scene.objects.get(object_id);
        return object.get_local_bounds().then((bounds) => {
            return IBoundingBox.get_face_plane(bounds.aabb, direction);
        });
    }
    get_perspective_framed_bounds(bounds, projection, direction, loose_up, framing_target, margin_world, margin_viewport) {
        return get_perspective_framed_bounds(bounds, projection, direction, loose_up, framing_target, margin_world, margin_viewport);
    }
    get_precise_framed_perspective_camera_matrix(object_ids, projection, direction, loose_up, include_child_objects, margin_world, margin_viewport) {
        return get_precise_framed_perspective_camera_matrix(this, object_ids, projection, direction, loose_up, include_child_objects, margin_world, margin_viewport);
    }
    /**
     * Sets the camera exposure on the scene without dirtying
     * @returns true if the value on the scene was changed
     */
    set_camera_exposure_internal(ev) {
        let exposure;
        if (typeof ev === 'number') {
            exposure = ev_to_intensity(ev);
        }
        else {
            const photographic_exposure = typeof ev === 'string' ? EXPOSURE_PRESETS[ev] : ev;
            exposure = ev_to_intensity(exposure_value(photographic_exposure));
        }
        if (this.scene.imageProcessingConfiguration.exposure !== exposure) {
            this.scene.imageProcessingConfiguration.exposure = exposure;
            return true;
        }
        else {
            return false;
        }
    }
    set_camera_exposure(ev) {
        if (this.set_camera_exposure_internal(ev)) {
            this.mark_scene_dirty();
        }
    }
    get_camera_exposure() {
        return -Math.log2(this.scene.imageProcessingConfiguration.exposure);
    }
    create_ui_mesh() {
        return new UiMesh(this);
    }
    create_soft_shadow_plane_renderer(resolution) {
        const renderer = new SoftShadowRenderer(this, resolution);
        this._light_manager.experimental_soft_shadow_renderers.add(renderer);
        return renderer;
    }
    delete_soft_shadow_plane_renderer(renderer) {
        const full_renderer = renderer;
        if (this._light_manager.experimental_soft_shadow_renderers.delete(full_renderer)) {
            full_renderer.dispose();
        }
    }
    get_webgl_memory_tracker() {
        return this._webgl_memory_tracker;
    }
    create_ghost_material() {
        return new GhostMaterial(this);
    }
    get_input_handler_manager() {
        return this._input_handler_manager;
    }
    create_input_handler(execute_object, interaction_objects) {
        return this._input_handler_manager.create_input_handler(execute_object, interaction_objects);
    }
    set_light_distance_falloff_enabled(light, enabled) {
        light.expermiental_distance_falloff_enabled = enabled;
    }
    calculate_inverse_tonemapped_color(color) {
        if (this._float_framebuffer_enabled) {
            const inverse_tonemapped_color = get_fitted_inverse_tonemap(VIEWER_DEFAULT_FLOAT_FRAMEBUFFER_TYPE, color.r, color.g, color.b, false);
            return inverse_tonemapped_color;
        }
        else {
            return IColor.to_linear_space(color);
        }
    }
}
/**
 * Keep track of the number of Viewer instances running.
 * Will increment on constructor, and decrement in the dispose function.
 */
Viewer._viewer_instances_running = 0;
__decorate([
    Deprecated({ new: 'PrimitiveBuilder.create_box' })
], Viewer.prototype, "create_box", null);
__decorate([
    Deprecated({ new: 'PrimitiveBuilder.create_sphere' })
], Viewer.prototype, "create_sphere", null);
__decorate([
    Deprecated({ new: 'PrimitiveBuilder.create_spherical_dome' })
], Viewer.prototype, "create_light_mesh", null);
__decorate([
    Deprecated({ new: 'PrimitiveBuilder.create_plane' })
], Viewer.prototype, "create_plane", null);
__decorate([
    Deprecated({ new: 'FocusBorder module' })
], Viewer.prototype, "set_focus_border", null);
__decorate([
    Deprecated({ new: 'FocusBorder module' })
], Viewer.prototype, "get_focus_border", null);
__decorate([
    Deprecated({ new: 'UiLine module' })
], Viewer.prototype, "create_ui_line", null);
__decorate([
    Deprecated({ new: 'UiLine module' })
], Viewer.prototype, "update_ui_line", null);
__decorate([
    Deprecated({ new: 'UiLine module' })
], Viewer.prototype, "delete_ui_line", null);
__decorate([
    Deprecated({ new: 'UiLine module' })
], Viewer.prototype, "set_ui_line_visible", null);
__decorate([
    Deprecated({ new: 'UiLine module' })
], Viewer.prototype, "get_ui_line_visible", null);
__decorate([
    Deprecated({ new: 'acquire_rendering_pause_lock' })
], Viewer.prototype, "pause_rendering", null);
__decorate([
    Deprecated({ new: 'release_rendering_pause_lock' })
], Viewer.prototype, "resume_rendering", null);
//# sourceMappingURL=Viewer.js.map